/**
 * Client Services – Provider Search
 * Need record list + Search Providers (filters, email BCC, export CSV).
 * Add need from here or via Client record → "Client needs" (clientId/referralId in URL).
 */
import React, { Fragment, useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import { flushSync, createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import { useTenantFromUrl } from '../hooks/useTenantFromUrl';
import { useDebouncedSearch } from '../hooks/useDebouncedSearch';
import { useInfiniteScroll } from '../hooks/useInfiniteScroll';
import { useAutoSave } from '../hooks/useAutoSave';
import { clientServicesAPI, clientAPI, referralAPI, providerAPI, clientProviderServiceAPI } from '../services/api';
import api from '../services/api';
import { hasPermission, getEffectiveRoleCode } from '../utils/permissions';
import openMailtoPreferNewTab from '../utils/openMailtoPreferNewTab';
import { formatDisplayDate } from '../utils/dateFormat';
import { formatProviderDisplayLabel, formatProviderSiteOptionLabel } from '../utils/providerDisplayLabel';
import SearchableProviderSelect from '../components/SearchableProviderSelect';
import SearchableProviderTeamOfficeSelect from '../components/SearchableProviderTeamOfficeSelect';
import PageHeader from '../components/PageHeader';
import InfoIconButton from '../components/InfoIconButton';
import LightFilterSelect from '../components/LightFilterSelect';
import '../styles/FormGrid.css';
import '../components/SideNavLayout.css';
import './ProviderSearch.css';

const SECTIONS_NAV_COLLAPSE_STORAGE_KEY = 'cfc-provider-search-sections-nav-collapsed';

const PAGE_SIZE = 25;

/** When code and name differ, show `CODE — name`; when same (case-insensitive), show once. */
function directoryOptionLabel(code, name) {
  const c = String(code ?? '').trim();
  const n = String(name ?? '').trim();
  if (!c && !n) return '—';
  if (!c) return n;
  if (!n) return c;
  if (c.localeCompare(n, undefined, { sensitivity: 'accent' }) === 0) return n;
  if (c.toUpperCase() === n.toUpperCase()) return n.length >= c.length ? n : c;
  return `${c} — ${n}`;
}

function directoryFilterTriggerSummary(appliedIds, options) {
  if (!appliedIds.length) return 'All';
  if (appliedIds.length === 1) {
    const opt = options.find((o) => o.id === appliedIds[0]);
    const raw = opt?.label || '1';
    return raw.length > 30 ? `${raw.slice(0, 28)}…` : raw;
  }
  return `${appliedIds.length} selected`;
}

/** CSV: comma-separated labels for applied filter ids, or "All" when that dimension was not narrowed. */
function directoryFilterExportLabels(appliedIdStrs, options) {
  if (!appliedIdStrs || !appliedIdStrs.length) return 'All';
  const labels = appliedIdStrs
    .map((id) => options.find((o) => String(o.id) === String(id))?.label)
    .filter(Boolean);
  return labels.length ? labels.join(', ') : 'All';
}

function DirectoryFilterFunnelIcon() {
  return (
    <svg className="provider-search-dir-funnel-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M4 6H20L14 13V18L10 20V13L4 6Z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/** Format primary office + provider record fields from central-availability match API row. */
function formatMatchRowContactSummary(row) {
  if (!row) return null;
  const email = (row.provider_email || '').trim();
  const phone = (row.provider_phone || '').trim();
  const street = [row.primary_site_street_1, row.primary_site_street_2]
    .map((s) => (s != null ? String(s).trim() : ''))
    .filter(Boolean)
    .join(', ');
  const city = (row.primary_site_city || '').trim();
  const st = (row.primary_site_state || '').trim();
  const zip = (row.primary_site_zip || '').trim();
  const cityLine = [city, [st, zip].filter(Boolean).join(' ')].filter(Boolean).join(', ');
  const sitePhone = (row.primary_site_phone || '').trim();
  const siteEmail = (row.primary_site_email || '').trim();
  const hasAddress = Boolean(street || cityLine);
  return { email, phone, street, cityLine, sitePhone, siteEmail, hasAddress };
}


/** Prefer snake_case API field, optional camelCase fallback (if a proxy ever renames keys). */
function pickNeedField(need, snakeKey, camelKey) {
  if (!need) return undefined;
  const usable = (v) => {
    if (v === undefined || v === null) return false;
    if (typeof v === 'string' && v.trim() === '') return false;
    return true;
  };
  if (usable(need[snakeKey])) return need[snakeKey];
  if (camelKey && usable(need[camelKey])) return need[camelKey];
  return undefined;
}

/** Normalize date for <input type="date"> (expects YYYY-MM-DD). API may send ISO string. */
function toDateInputValue(value) {
  if (value == null || value === '') return '';
  const s = typeof value === 'string' ? value : (value instanceof Date ? value.toISOString() : String(value));
  return s.slice(0, 10) || '';
}

/** Stable picker value from API row (computed alias and/or FK columns after PATCH RETURNING *). */
function actualProviderOfficeValueFromNeed(need) {
  if (!need) return '';
  const rawSource = need.actual_provider_office_value ?? need.actualProviderOfficeValue;
  const raw = rawSource != null ? String(rawSource).trim() : '';
  if (raw) return raw;
  const payee = pickNeedField(need, 'actual_payee_site_id', 'actualPayeeSiteId');
  if (payee != null) return String(payee).trim();
  const ps = pickNeedField(need, 'actual_provider_site_id', 'actualProviderSiteId');
  if (ps != null) return String(ps).trim();
  const ap = pickNeedField(need, 'actual_provider_id', 'actualProviderId');
  if (ap != null) return `provider:${String(ap).trim()}`;
  return '';
}

/** Map GET need response → Actual provider office picker fields (value, label, provider id). */
function needToActualProviderOfficeFormFields(need) {
  if (!need) {
    return { actual_provider_id: '', actual_provider_office_value: '', actualProviderOfficeDisplayLabel: '' };
  }
  const officeVal = actualProviderOfficeValueFromNeed(need);
  const apId = pickNeedField(need, 'actual_provider_id', 'actualProviderId');
  const displayLabel = officeVal
    ? formatProviderSiteOptionLabel({
        first_name: pickNeedField(need, 'actual_provider_first_name', 'actualProviderFirstName'),
        last_name: pickNeedField(need, 'actual_provider_last_name', 'actualProviderLastName'),
        payee_name: pickNeedField(need, 'actual_provider_payee_name', 'actualProviderPayeeName'),
        provider_type_code: pickNeedField(need, 'actual_provider_type_code', 'actualProviderTypeCode'),
        site_street_1: pickNeedField(need, 'actual_office_site_street_1', 'actualOfficeSiteStreet1'),
        site_city: pickNeedField(need, 'actual_office_site_city', 'actualOfficeSiteCity'),
        site_label: pickNeedField(need, 'actual_office_site_label', 'actualOfficeSiteLabel') ?? '',
        provider_id: apId,
        id: apId,
      })
    : '';
  return {
    actual_provider_id: apId != null ? String(apId) : '',
    actual_provider_office_value: officeVal,
    actualProviderOfficeDisplayLabel: displayLabel,
  };
}

/** Default shape for the edit-need modal (before GET fills fields). */
function emptyEditNeedFormFields() {
  return {
    need_type_id: '',
    service_area_id: '',
    need_start: '',
    frequency_id: '',
    status: 'open',
    need_close: '',
    need_feeding: false,
    sc_note: '',
    family_availability: '',
    diagnostic_reason: '',
    autism_family_discussed: null,
    actual_provider_id: '',
    actual_provider_office_value: '',
    actualProviderOfficeDisplayLabel: '',
    service_delivery_mode: '',
    service_delivery_start_date: '',
    delay_start_date: '',
    delay_reason_id: '',
    delay_end_date: '',
    delay_reported_to_dhs_date: '',
    delay_comment: '',
    issue_type: null,
  };
}

/**
 * Map GET need → edit form. If the response has no actual provider but `prev` does, keep `prev`'s
 * picker fields so a stale GET (read before PATCH committed, or duplicate in-flight request) cannot
 * wipe a selection the user just saved.
 */
function mergeServerNeedIntoEditForm(prev, need) {
  if (!need) return prev;
  const fromGet = {
    need_type_id: need.need_type_id != null ? String(need.need_type_id) : '',
    service_area_id: need.service_area_id != null ? String(need.service_area_id) : '',
    need_start: need.need_start ? need.need_start.split('T')[0] : '',
    frequency_id: need.frequency_id != null ? String(need.frequency_id) : '',
    status: need.status || 'open',
    need_close: need.need_close ? need.need_close.split('T')[0] : '',
    need_feeding: need.need_feeding === true,
    sc_note: need.sc_note || '',
    family_availability: need.family_availability || '',
    diagnostic_reason: need.diagnostic_reason ? String(need.diagnostic_reason) : '',
    autism_family_discussed: need.autism_family_discussed !== undefined && need.autism_family_discussed !== null ? need.autism_family_discussed : null,
    service_delivery_mode: need.service_delivery_mode || '',
    service_delivery_start_date: need.service_delivery_start_date ? need.service_delivery_start_date.split('T')[0] : '',
    delay_start_date: need.delay_start_date ? need.delay_start_date.split('T')[0] : '',
    delay_reason_id: need.delay_reason_id != null ? String(need.delay_reason_id) : '',
    delay_end_date: need.delay_end_date ? need.delay_end_date.split('T')[0] : '',
    delay_reported_to_dhs_date: need.delay_reported_to_dhs_date ? need.delay_reported_to_dhs_date.split('T')[0] : '',
    delay_comment: need.delay_comment || '',
    issue_type: need.issue_type || null,
    ...needToActualProviderOfficeFormFields(need),
  };
  const getHasPick =
    (fromGet.actual_provider_office_value || '').trim() !== '' ||
    (fromGet.actual_provider_id || '').trim() !== '';
  const prevHasPick =
    (prev.actual_provider_office_value || '').trim() !== '' ||
    (prev.actual_provider_id || '').trim() !== '';
  if (!getHasPick && prevHasPick) {
    return {
      ...fromGet,
      actual_provider_office_value: prev.actual_provider_office_value,
      actualProviderOfficeDisplayLabel: prev.actualProviderOfficeDisplayLabel,
      actual_provider_id: prev.actual_provider_id,
    };
  }
  return fromGet;
}

/** Strip time from LVV history lines (e.g. "1/14/2026 0:00:00" -> "1/14/2026") so only dates are shown. */
function normalizeLvvHistoryForDisplay(history) {
  if (history == null || typeof history !== 'string') return '';
  return history
    .split(/\r?\n/)
    .map((line) => line.replace(/\s+\d{1,2}:\d{2}(:\d{2})?/g, '').trim())
    .join('\n');
}

/** User-facing line from API `central_availability_ip_notify` after save (in-person SC inbox alerts). */
function centralIpNotifyFeedback(notify) {
  if (!notify || typeof notify !== 'object') return null;
  const sent = Number(notify.sent) || 0;
  if (sent > 0) {
    return {
      variant: 'success',
      text:
        sent === 1
          ? 'Inbox notification sent to one service coordinator who has open needs matching this service type and service area.'
          : `Inbox notifications sent to ${sent} service coordinators who have open needs matching this service type and service area.`,
    };
  }
  const skip = notify.skipped;
  if (typeof skip !== 'string' || !skip.trim()) return null;
  if (/not new or provider, need type, and service area are unchanged/i.test(skip)) return null;
  return { variant: 'info', text: skip };
}

/** Must match backend clientServicesController CENTRAL_AVAILABILITY_EMAIL_ALL_FOUR_THERAPY_AGY_CTR */
const CENTRAL_AVAILABILITY_EMAIL_ALL_FOUR_THERAPY_AGY_CTR = '__ALL_FOUR_DT_OT_PT_ST_PAYEE_AGY_CTR__';

/** Provider types allowed for client services: OT, PT, DT, ST (speech), Diagnostic */
const CLIENT_SERVICE_TYPE_CODES = new Set(['OT', 'PT', 'DT', 'ST', 'DGN']);
const CLIENT_SERVICE_NAME_TERMS = ['occupational', 'physical', 'developmental', 'speech', 'diagnostic'];
function isClientServiceProviderType(pt) {
  if (!pt) return false;
  const code = (pt.code || '').toUpperCase().trim();
  const name = (pt.name || '').toLowerCase();
  if (CLIENT_SERVICE_TYPE_CODES.has(code)) return true;
  if (CLIENT_SERVICE_NAME_TERMS.some((term) => name.includes(term))) return true;
  return false;
}

/** Extra types shown only on Client Service **needs** (add/edit + needs list filter). Not for Email by Type or provider search. */
const CLIENT_SERVICE_NEED_EXTRA_TYPE_CODES = new Set(['PSYC', 'NU', 'DTH', 'DTV']);
function isClientServiceNeedType(pt) {
  if (!pt) return false;
  const code = (pt.code || '').toUpperCase().trim();
  if (CLIENT_SERVICE_NEED_EXTRA_TYPE_CODES.has(code)) return true;
  return isClientServiceProviderType(pt);
}

/** DGN diagnostic reason — values must match `cs_needs.diagnostic_reason` CHECK and backend. */
const DGN_DIAGNOSTIC_REASON_OPTIONS = [
  { value: 'autism', label: 'Autism' },
  { value: 'lack_of_progress', label: 'Lack of progress' },
  { value: 'motor', label: 'Motor' },
  { value: 'other', label: 'Other' },
  { value: 'sensory', label: 'Sensory' },
  { value: 'vision', label: 'Vision' },
];

function needTypeRowIsDgn(pt) {
  return pt && (pt.code || '').toUpperCase().trim() === 'DGN';
}

function needTypeIdIsDgn(providerTypes, needTypeId) {
  if (!needTypeId) return false;
  const row = providerTypes.find((p) => String(p.id) === String(needTypeId));
  return row ? needTypeRowIsDgn(row) : false;
}

/** True if provider is OT, PT, DT, ST, or Diagnostic (for Central Availability dropdown). */
function isClientServiceProvider(provider) {
  if (!provider) return false;
  const code = (provider.provider_type_code || '').toUpperCase().trim();
  const name = (provider.provider_type_name || '').toLowerCase();
  if (CLIENT_SERVICE_TYPE_CODES.has(code)) return true;
  if (CLIENT_SERVICE_NAME_TERMS.some((term) => name.includes(term))) return true;
  return false;
}

const emptyNeedForm = () => ({
  client_id: '',
  referral_id: '',
  need_type_id: '',
  service_area_id: '',
  need_start: '',
  frequency_id: '',
  need_feeding: false,
  sc_note: '',
  family_availability: '',
  diagnostic_reason: '',
  autism_family_discussed: null,
});

export default function ProviderSearch({ clientIdOverride = null, referralIdOverride = null, defaultNeedsStatusFilter = null }) {
  useTenantFromUrl();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlClientId = searchParams.get('clientId') || searchParams.get('client_id') || '';
  const urlReferralId = searchParams.get('referralId') || searchParams.get('referral_id') || '';
  // When embedded in client layout, use override so first render has client context (avoids race where we load all needs before searchParams are set)
  const effectiveClientId = (clientIdOverride != null && clientIdOverride !== '') ? String(clientIdOverride) : urlClientId;
  const effectiveReferralId = (referralIdOverride != null && referralIdOverride !== '') ? String(referralIdOverride) : urlReferralId;

  const [needs, setNeeds] = useState([]);
  const [needsTotal, setNeedsTotal] = useState(0);
  const [needsPage, setNeedsPage] = useState(1);
  const [needsLoading, setNeedsLoading] = useState(true);
  const [needsLoadingMore, setNeedsLoadingMore] = useState(false);
  const needsSentinelRef = useRef(null);
  const [activeSection, setActiveSection] = useState('needs'); // 'needs' | 'search' | 'central'
  // Services list filters (compact row, same idea as File Reviews). When opened from Edit Client, default to All so count matches list.
  const [needsStatusFilter, setNeedsStatusFilter] = useState(() => defaultNeedsStatusFilter || 'open');
  const [needsClientSearch, setNeedsClientSearch] = useState('');
  const [needsTypeFilter, setNeedsTypeFilter] = useState('');
  const [needsFrequencyFilter, setNeedsFrequencyFilter] = useState('');
  const [needsServiceAreaFilter, setNeedsServiceAreaFilter] = useState('');
  // Availability view toggle: 'standard' | 'availability'
  const [needsView, setNeedsView] = useState('standard');
  const [availSort, setAvailSort] = useState({ by: 'avail', dir: 'desc' }); // desc = populated first

  // Add need form
  const [showAddForm, setShowAddForm] = useState(false);
  const [needForm, setNeedForm] = useState(emptyNeedForm());
  const [addFormSaving, setAddFormSaving] = useState(false);
  const [addFormError, setAddFormError] = useState(null);
  const [clients, setClients] = useState([]);
  const [addFormStaffId, setAddFormStaffId] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [needFormProviderTypes, setNeedFormProviderTypes] = useState([]);
  const [delayReasons, setDelayReasons] = useState([]);
  const [needFormServiceAreas, setNeedFormServiceAreas] = useState([]);
  const [frequencies, setFrequencies] = useState([]);
  const [clientNameFromUrl, setClientNameFromUrl] = useState(null);

  // Edit need form (same fields as add for full edit)
  const [editingNeedId, setEditingNeedId] = useState(null);
  // Delay Information section is collapsed by default; auto-expanded when the need already has a delay.
  const [delayInfoOpen, setDelayInfoOpen] = useState(false);
  const [editForm, setEditForm] = useState(() => emptyEditNeedFormFields());
  const [editFormLoading, setEditFormLoading] = useState(false);
  const [editFormSaving, setEditFormSaving] = useState(false);
  const [editFormSaveMessage, setEditFormSaveMessage] = useState(null);
  /** Edit Need uses explicit Save (no autosave); true after any local change until save succeeds or load resets. */
  const [editNeedDirty, setEditNeedDirty] = useState(false);
  const [editFormError, setEditFormError] = useState(null);
  const [editFormClientName, setEditFormClientName] = useState('');
  const [editFormClientDob, setEditFormClientDob] = useState('');
  const [editFormAssignedScName, setEditFormAssignedScName] = useState('');
  /** Always read this inside async save (autosave timeout); avoids stale closure over editForm. */
  const editFormRef = useRef(editForm);
  editFormRef.current = editForm;
  const editingNeedIdRef = useRef(editingNeedId);
  editingNeedIdRef.current = editingNeedId;
  /** True after user clears the Actual provider picker (so next save sends explicit nulls). */
  const needActualProviderExplicitClearRef = useRef(false);
  const [familyAvailabilityHintOpen, setFamilyAvailabilityHintOpen] = useState(false);
  const [scNoteHintOpen, setScNoteHintOpen] = useState(false);
  const [needCloseDateHintEditOpen, setNeedCloseDateHintEditOpen] = useState(false);
  const [deliveryStartHintEditOpen, setDeliveryStartHintEditOpen] = useState(false);
  const [matchHintOpen, setMatchHintOpen] = useState(false);
  const [expandedCommentRowId, setExpandedCommentRowId] = useState(null);
  /** Expanded inline contact panel for a central-availability match row (click row to toggle). */
  const [matchContactOpenRowId, setMatchContactOpenRowId] = useState(null);
  // Need referral_id for "Commit match" (add provider to team)
  const [needReferralId, setNeedReferralId] = useState(null);
  // Match to availability (minimal path: find matches → commit or try next)
  const [showMatchList, setShowMatchList] = useState(false);
  const [matchList, setMatchList] = useState([]);
  const [matchListLoading, setMatchListLoading] = useState(false);
  const [matchListError, setMatchListError] = useState(null);
  const [commitMatchError, setCommitMatchError] = useState(null);
  const [commitMatchSuccess, setCommitMatchSuccess] = useState(null);
  const [committingMatch, setCommittingMatch] = useState(false);
  // Match dialog: only ask service delivery mode; frequency & start date copied from need
  const [matchDialogRow, setMatchDialogRow] = useState(null);
  const [matchDialogMode, setMatchDialogMode] = useState('');
  const [matchDialogDeliveryStart, setMatchDialogDeliveryStart] = useState('');

  // Search providers state
  const [providerTypes, setProviderTypes] = useState([]);
  const [serviceAreas, setServiceAreas] = useState([]);
  const [credentialCodes, setCredentialCodes] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [filters, setFilters] = useState({
    providerTypeIds: [],
    serviceAreaIds: [],
    credentialIds: [],
    languageIds: [],
    nonAssistant: false,
  });
  /** Draft selections until Apply (per panel), like Search Providers report filters. */
  const [dirDraftProviderTypeIds, setDirDraftProviderTypeIds] = useState([]);
  const [dirDraftServiceAreaIds, setDirDraftServiceAreaIds] = useState([]);
  const [dirDraftCredentialIds, setDirDraftCredentialIds] = useState([]);
  const [dirDraftLanguageIds, setDirDraftLanguageIds] = useState([]);
  const directorySearchPrevSectionRef = useRef(null);
  /** Latest applied directory filters; read when entering Search so drafts sync without listing `filters` in effect deps. */
  const directorySearchFiltersRef = useRef(filters);
  directorySearchFiltersRef.current = filters;
  /** Directory Search: compact funnel triggers; popover lists checkboxes + Apply (like report column filters). */
  const [dirFilterPopoverKey, setDirFilterPopoverKey] = useState(null);
  const [dirFilterPopoverPos, setDirFilterPopoverPos] = useState({ top: 0, left: 0, width: 300 });
  const dirFilterTriggerRefs = useRef({});
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [actionsDropdownOpen, setActionsDropdownOpen] = useState(false);
  const actionsDropdownRef = useRef(null);

  /** Search Providers: directory (master list) vs proven (matched needs, need's type + area). */
  const [providerSearchMode, setProviderSearchMode] = useState('directory');
  const [provenNeedTypeIds, setProvenNeedTypeIds] = useState([]);
  const [provenServiceAreaIds, setProvenServiceAreaIds] = useState([]);
  const [provenResults, setProvenResults] = useState([]);
  const [provenSearching, setProvenSearching] = useState(false);
  const [provenError, setProvenError] = useState(null);
  const [provenHasSearched, setProvenHasSearched] = useState(false);
  const [directorySearchInfoOpen, setDirectorySearchInfoOpen] = useState(false);
  const [provenNeedsInfoOpen, setProvenNeedsInfoOpen] = useState(false);
  const [sectionsNavCollapsed, setSectionsNavCollapsed] = useState(() => {
    try {
      return window.sessionStorage.getItem(SECTIONS_NAV_COLLAPSE_STORAGE_KEY) === '1';
    } catch (_) {
      return false;
    }
  });

  const toggleSectionsNav = () => {
    setSectionsNavCollapsed((c) => {
      const next = !c;
      try {
        window.sessionStorage.setItem(SECTIONS_NAV_COLLAPSE_STORAGE_KEY, next ? '1' : '0');
      } catch (_) {}
      return next;
    });
  };

  const canManageNeeds = hasPermission('client_services.manage_needs');
  const canViewNeeds = canManageNeeds || hasPermission('client_services.view_all');
  const canReclassifyNeed = hasPermission('cs_need.reclassify');
  const openedNeedFromUrlRef = useRef(null);
  const editFormNeedIsDgn = useMemo(
    () => needTypeIdIsDgn(needFormProviderTypes, editForm.need_type_id),
    [needFormProviderTypes, editForm.need_type_id]
  );
  const addFormNeedIsDgn = useMemo(
    () => needTypeIdIsDgn(needFormProviderTypes, needForm.need_type_id),
    [needFormProviderTypes, needForm.need_type_id]
  );

  useEffect(() => {
    const needIdParam = searchParams.get('needId');
    if (!needIdParam) {
      openedNeedFromUrlRef.current = null;
      return;
    }
    if (!canViewNeeds) return;
    if (openedNeedFromUrlRef.current === needIdParam) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await clientServicesAPI.getNeed(needIdParam);
        const need = res.data?.data ?? res.data;
        if (cancelled || !need?.id) return;
        openedNeedFromUrlRef.current = needIdParam;
        setActiveSection('needs');
        setNeedCloseDateHintEditOpen(false);
        setEditingNeedId(need.id);
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          next.delete('needId');
          return next;
        }, { replace: true });
      } catch (_) {
        /* not found or forbidden */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [searchParams, canViewNeeds, setSearchParams]);

  const canSearchProviders = hasPermission('client_services.search_providers');
  const canManageCentralAvailability = hasPermission('client_services.manage_central_availability');
  const canDeleteCentralAvailability = hasPermission('client_services.delete_central_availability');

  // Central Availability: list + add/edit
  const [centralList, setCentralList] = useState([]);
  const [centralTotal, setCentralTotal] = useState(0);
  const [centralPage, setCentralPage] = useState(1);
  const [centralLoading, setCentralLoading] = useState(false);
  const [centralTypeFilter, setCentralTypeFilter] = useState('');
  const [centralAreaFilter, setCentralAreaFilter] = useState('');
  const [centralProviderPayeeSearch, setCentralProviderPayeeSearch] = useState('');
  const [showCentralAddForm, setShowCentralAddForm] = useState(false);
  /** Remount SearchableProviderSelect when opening Add so label state resets */
  const [centralAddProviderFieldKey, setCentralAddProviderFieldKey] = useState(0);
  const [centralForm, setCentralForm] = useState({ provider_id: '', service_area_id: '', need_type_id: '', ip_date: '', lvv_date: '', lvv_history: '', provider_comment: '', also_feeding: false });
  const [editingCentralId, setEditingCentralId] = useState(null);
  const [centralEditForm, setCentralEditForm] = useState({ provider_id: '', service_area_id: '', need_type_id: '', ip_date: '', lvv_date: '', lvv_history: '', provider_comment: '', also_feeding: false });
  const [centralFormSaving, setCentralFormSaving] = useState(false);
  const [centralDeleting, setCentralDeleting] = useState(false);
  const [centralSaveMessage, setCentralSaveMessage] = useState(null);
  /** Above Central Availability list: SC inbox notifications after save (add or edit — edit closes modal right away) */
  const [centralSectionIpNotify, setCentralSectionIpNotify] = useState(null);
  const centralIpNotifyDismissRef = useRef(null);
  const [centralEditSaveTrigger, setCentralEditSaveTrigger] = useState(0);
  /** LVV date when edit was opened; when user changes LVV date on save, this value is prepended to LVV history */
  const [centralEditOriginalLvvDate, setCentralEditOriginalLvvDate] = useState('');
  const [centralFormError, setCentralFormError] = useState(null);
  const [centralServiceAreas, setCentralServiceAreas] = useState([]);
  const [centralProviderTypes, setCentralProviderTypes] = useState([]);
  const [centralSortBy, setCentralSortBy] = useState('provider');
  const [centralSortDir, setCentralSortDir] = useState('asc');
  const [emailByTypeNeedTypeId, setEmailByTypeNeedTypeId] = useState('');
  const [emailByTypeLoading, setEmailByTypeLoading] = useState(false);
  const [emailByTypeError, setEmailByTypeError] = useState(null);
  const [emailByTypeSendLoading, setEmailByTypeSendLoading] = useState(false);
  const [emailByTypeSendError, setEmailByTypeSendError] = useState(null);
  const [emailByTypeSendSuccess, setEmailByTypeSendSuccess] = useState(null);
  const [sendPreviewOpen, setSendPreviewOpen] = useState(false);
  const [sendPreviewData, setSendPreviewData] = useState(null);
  const [sendPreviewLoading, setSendPreviewLoading] = useState(false);
  const [emailByTypeInfoOpen, setEmailByTypeInfoOpen] = useState(false);
  const [centralLvvHistoryHintOpen, setCentralLvvHistoryHintOpen] = useState(false);
  /** Until provider-types load, show type from central row */
  const [centralEditNeedTypeSnapshot, setCentralEditNeedTypeSnapshot] = useState('');

  // Central Availability section only when tenant has the feature enabled
  const [showCentralAvailability, setShowCentralAvailability] = useState(false);
  useEffect(() => {
    api.get('/whoami')
      .then((res) => {
        const on = res.data?.features?.client_services_central_availability === true;
        setShowCentralAvailability(!!on);
      })
      .catch(() => setShowCentralAvailability(false));
  }, []);
  const isServiceCoordinator = getEffectiveRoleCode() === 'SERVICE_COORDINATOR';
  // If we're on central but feature is off, in client context, or user is SERVICE_COORDINATOR, switch to needs
  useEffect(() => {
    if (activeSection === 'central' && (!showCentralAvailability || effectiveClientId || isServiceCoordinator)) {
      setActiveSection('needs');
    }
  }, [showCentralAvailability, activeSection, effectiveClientId, isServiceCoordinator]);

  // When opening edit, clear stale fields then load the need (merge preserves provider if GET is stale vs PATCH)
  useEffect(() => {
    if (!editingNeedId || !canViewNeeds) return;
    let cancelled = false;
    const requestedId = editingNeedId;
    setEditFormLoading(true);
    setEditFormError(null);
    setEditForm(() => emptyEditNeedFormFields());
    clientServicesAPI.getNeed(requestedId)
      .then((res) => {
        if (cancelled) return;
        if (String(editingNeedIdRef.current) !== String(requestedId)) return;
        const need = res.data?.data ?? res.data;
        if (need) {
          setNeedReferralId(need.referral_id || null);
          setCommitMatchSuccess(null);
          const clientName = [need.client_last_name, need.client_first_name].filter(Boolean).join(', ') || (need.client_id ? `Client ${need.client_id}` : '—');
          setEditFormClientName(clientName);
          setEditFormAssignedScName(need.client_assigned_sc_name || need.clientAssignedScName || need.assigned_sc_name || need.assignedScName || '');
          setEditForm((prev) => mergeServerNeedIntoEditForm(prev, need));
          setDelayInfoOpen(!!need.delay_start_date);
          setEditNeedDirty(false);
        }
      })
      .catch(() => {
        if (cancelled) return;
        if (String(editingNeedIdRef.current) !== String(requestedId)) return;
        setEditFormError('Could not load service.');
      })
      .finally(() => {
        if (!cancelled && String(editingNeedIdRef.current) === String(requestedId)) {
          setEditFormLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [editingNeedId, canViewNeeds]);

  // URL or override has clientId: load client name and referrals for Add need pre-fill
  useEffect(() => {
    if (!effectiveClientId || !canViewNeeds) return;
    clientAPI.getById(effectiveClientId).then((r) => {
      const c = r.data?.data ?? r.data;
      setClientNameFromUrl(c ? `${c.last_name || ''}, ${c.first_name || ''}`.replace(/^,\s*|,\s*$/g, '').trim() || `Client ${effectiveClientId}` : null);
    }).catch(() => setClientNameFromUrl(null));
    referralAPI.getByClientId(effectiveClientId).then((r) => {
      const list = r.data?.data ?? r.data ?? [];
      setReferrals(list);
      if (list.length > 0) {
        const refId = effectiveReferralId && list.some((ref) => String(ref.id) === String(effectiveReferralId))
          ? effectiveReferralId
          : String(list[0].id);
        setNeedForm((f) => ({ ...f, client_id: effectiveClientId, referral_id: refId }));
      }
    }).catch(() => setReferrals([]));
  }, [effectiveClientId, effectiveReferralId, canViewNeeds]);

  // When on Services tab or opening Add/Edit form, load lookups so filter dropdowns and forms have data
  useEffect(() => {
    if ((activeSection !== 'needs' && !showAddForm && !editingNeedId) || !canViewNeeds) return;
    if (showAddForm) {
      // Fetch current user's staff_id so the client list is scoped to their caseload.
      // Backend auto-scopes SCs anyway; this ensures wider-scope users (trainers, managers)
      // also see only their own assigned clients in the Add Need dropdown.
      api.get('/whoami').then((res) => {
        const sid = res.data?.user?.staff_id ?? null;
        setAddFormStaffId(sid);
        const params = { pageSize: 200, status: 'open' };
        if (sid) params.assigned_staff_id = sid;
        clientAPI.getAll(params).then((r) => setClients(r.data?.data ?? r.data ?? [])).catch(() => setClients([]));
      }).catch(() => {
        clientAPI.getAll({ pageSize: 200, status: 'open' }).then((r) => setClients(r.data?.data ?? r.data ?? [])).catch(() => setClients([]));
      });
    }
    api.get('/provider-types').then((r) => setNeedFormProviderTypes(r.data?.data ?? r.data ?? [])).catch(() => setNeedFormProviderTypes([]));
    api.get('/service-areas').then((r) => setNeedFormServiceAreas(r.data?.data ?? r.data ?? [])).catch(() => setNeedFormServiceAreas([]));
    api.get('/lookups/frequencies').then((r) => setFrequencies(r.data?.data ?? r.data ?? [])).catch(() => setFrequencies([]));
    api.get('/lookups/delay-reasons').then((r) => setDelayReasons(r.data?.data ?? r.data ?? [])).catch(() => setDelayReasons([]));
  }, [activeSection, showAddForm, editingNeedId, canViewNeeds]);

  // When user changes client in form, load referrals and auto-set current (first) referral
  const onNeedFormClientChange = (clientId) => {
    setNeedForm((f) => ({ ...f, client_id: clientId, referral_id: '' }));
    setReferrals([]);
    if (clientId) {
      referralAPI.getByClientId(clientId).then((r) => {
        const list = r.data?.data ?? r.data ?? [];
        setReferrals(list);
        if (list.length > 0) setNeedForm((f) => ({ ...f, referral_id: String(list[0].id) }));
      }).catch(() => setReferrals([]));
    }
  };

  useEffect(() => {
    if (canSearchProviders && activeSection === 'search') {
      api.get('/provider-types').then((r) => setProviderTypes(r.data?.data ?? r.data ?? [])).catch(() => setProviderTypes([]));
      api.get('/service-areas').then((r) => setServiceAreas(r.data?.data ?? r.data ?? [])).catch(() => setServiceAreas([]));
      clientServicesAPI.getCredentialCodes().then((r) => setCredentialCodes(r.data?.data ?? r.data ?? [])).catch(() => setCredentialCodes([]));
      api.get('/lookups/languages').then((r) => setLanguages(r.data?.data ?? r.data ?? [])).catch(() => setLanguages([]));
    }
  }, [canSearchProviders, activeSection]);

  /** When entering Search Providers, sync drafts from applied filters (not on every filter change). */
  useEffect(() => {
    if (!canSearchProviders || activeSection !== 'search') {
      directorySearchPrevSectionRef.current = activeSection;
      return;
    }
    const prev = directorySearchPrevSectionRef.current;
    directorySearchPrevSectionRef.current = activeSection;
    if (prev === 'search') return;
    const f = directorySearchFiltersRef.current;
    setDirDraftProviderTypeIds([...(f.providerTypeIds || [])]);
    setDirDraftServiceAreaIds([...(f.serviceAreaIds || [])]);
    setDirDraftCredentialIds([...(f.credentialIds || [])]);
    setDirDraftLanguageIds([...(f.languageIds || [])]);
  }, [activeSection, canSearchProviders]);

  const directoryProviderTypeOptions = useMemo(
    () =>
      [...providerTypes]
        .sort((a, b) => String(a.code || '').localeCompare(String(b.code || ''), undefined, { sensitivity: 'base' }))
        .map((pt) => ({
          id: String(pt.id),
          label: directoryOptionLabel(pt.code, pt.name),
        })),
    [providerTypes]
  );

  const directoryServiceAreaOptions = useMemo(
    () =>
      [...serviceAreas]
        .sort((a, b) =>
          String(a.name || a.code || '').localeCompare(String(b.name || b.code || ''), undefined, { sensitivity: 'base' })
        )
        .map((sa) => ({
          id: String(sa.id),
          label: directoryOptionLabel(sa.code, sa.name),
        })),
    [serviceAreas]
  );

  const directoryCredentialOptions = useMemo(
    () =>
      [...credentialCodes]
        .sort((a, b) => String(a.code || '').localeCompare(String(b.code || ''), undefined, { sensitivity: 'base' }))
        .map((cc) => ({
          id: String(cc.id),
          label: directoryOptionLabel(cc.code, cc.name),
        })),
    [credentialCodes]
  );

  const directoryLanguageOptions = useMemo(
    () =>
      [...languages]
        .sort((a, b) =>
          String(a.description || a.name || a.code || '').localeCompare(
            String(b.description || b.name || b.code || ''),
            undefined,
            { sensitivity: 'base' }
          )
        )
        .map((lang) => ({
          id: String(lang.id),
          label: directoryOptionLabel(lang.code, lang.description || lang.name),
        })),
    [languages]
  );

  const toggleDirDraft = useCallback((setter, idStr) => {
    setter((prev) => (prev.includes(idStr) ? prev.filter((x) => x !== idStr) : [...prev, idStr]));
  }, []);

  useLayoutEffect(() => {
    if (!dirFilterPopoverKey) return;
    const f = directorySearchFiltersRef.current;
    if (dirFilterPopoverKey === 'providerTypes') setDirDraftProviderTypeIds([...(f.providerTypeIds || [])]);
    else if (dirFilterPopoverKey === 'serviceAreas') setDirDraftServiceAreaIds([...(f.serviceAreaIds || [])]);
    else if (dirFilterPopoverKey === 'credentials') setDirDraftCredentialIds([...(f.credentialIds || [])]);
    else if (dirFilterPopoverKey === 'languages') setDirDraftLanguageIds([...(f.languageIds || [])]);
  }, [dirFilterPopoverKey]);

  useLayoutEffect(() => {
    if (!dirFilterPopoverKey) return undefined;
    const updatePosition = () => {
      const button = dirFilterTriggerRefs.current[dirFilterPopoverKey];
      if (!button) return;
      const rect = button.getBoundingClientRect();
      const width = Math.min(360, Math.max(280, window.innerWidth - 24));
      const viewportPadding = 8;
      const maxLeft = window.innerWidth - width - viewportPadding;
      const preferredLeft = rect.left + window.scrollX;
      const clampedLeft = Math.max(viewportPadding, Math.min(preferredLeft, maxLeft));
      setDirFilterPopoverPos({
        top: rect.bottom + window.scrollY + 6,
        left: clampedLeft,
        width,
      });
    };
    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [dirFilterPopoverKey]);

  useEffect(() => {
    if (!dirFilterPopoverKey) return undefined;
    const onPointerDown = (event) => {
      const t = event.target;
      if (t && t.closest && t.closest('.provider-search-dir-popover-portal')) return;
      if (t && t.closest && t.closest('.provider-search-dir-trigger-wrap')) return;
      setDirFilterPopoverKey(null);
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setDirFilterPopoverKey(null);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [dirFilterPopoverKey]);

  useEffect(() => {
    if (activeSection !== 'search' || providerSearchMode !== 'directory') setDirFilterPopoverKey(null);
  }, [activeSection, providerSearchMode]);

  const handleDirectoryFilterTrigger = (key, event) => {
    dirFilterTriggerRefs.current[key] = event.currentTarget;
    setDirFilterPopoverKey((prev) => (prev === key ? null : key));
  };

  const renderDirectoryFilterPopoverPortal = () => {
    if (!dirFilterPopoverKey) return null;
    let title;
    let options;
    let draftIds;
    let onToggleId;
    let onApply;
    let onClear;
    let appliedCount;
    let idleHint;
    if (dirFilterPopoverKey === 'providerTypes') {
      title = 'Provider types';
      options = directoryProviderTypeOptions;
      draftIds = dirDraftProviderTypeIds;
      onToggleId = (id) => toggleDirDraft(setDirDraftProviderTypeIds, id);
      onApply = () => {
        setFilters((f) => ({ ...f, providerTypeIds: [...dirDraftProviderTypeIds] }));
        setDirFilterPopoverKey(null);
      };
      onClear = () => {
        setDirDraftProviderTypeIds([]);
        setFilters((f) => ({ ...f, providerTypeIds: [] }));
        setDirFilterPopoverKey(null);
      };
      appliedCount = filters.providerTypeIds.length;
      idleHint = 'None applied — search includes all provider types.';
    } else if (dirFilterPopoverKey === 'serviceAreas') {
      title = 'Service areas';
      options = directoryServiceAreaOptions;
      draftIds = dirDraftServiceAreaIds;
      onToggleId = (id) => toggleDirDraft(setDirDraftServiceAreaIds, id);
      onApply = () => {
        setFilters((f) => ({ ...f, serviceAreaIds: [...dirDraftServiceAreaIds] }));
        setDirFilterPopoverKey(null);
      };
      onClear = () => {
        setDirDraftServiceAreaIds([]);
        setFilters((f) => ({ ...f, serviceAreaIds: [] }));
        setDirFilterPopoverKey(null);
      };
      appliedCount = filters.serviceAreaIds.length;
      idleHint = 'None applied — search includes all service areas.';
    } else if (dirFilterPopoverKey === 'credentials') {
      title = 'Credentials';
      options = directoryCredentialOptions;
      draftIds = dirDraftCredentialIds;
      onToggleId = (id) => toggleDirDraft(setDirDraftCredentialIds, id);
      onApply = () => {
        setFilters((f) => ({ ...f, credentialIds: [...dirDraftCredentialIds] }));
        setDirFilterPopoverKey(null);
      };
      onClear = () => {
        setDirDraftCredentialIds([]);
        setFilters((f) => ({ ...f, credentialIds: [] }));
        setDirFilterPopoverKey(null);
      };
      appliedCount = filters.credentialIds.length;
      idleHint = 'None applied — credential filter off.';
    } else if (dirFilterPopoverKey === 'languages') {
      title = 'Languages';
      options = directoryLanguageOptions;
      draftIds = dirDraftLanguageIds;
      onToggleId = (id) => toggleDirDraft(setDirDraftLanguageIds, id);
      onApply = () => {
        setFilters((f) => ({ ...f, languageIds: [...dirDraftLanguageIds] }));
        setDirFilterPopoverKey(null);
      };
      onClear = () => {
        setDirDraftLanguageIds([]);
        setFilters((f) => ({ ...f, languageIds: [] }));
        setDirFilterPopoverKey(null);
      };
      appliedCount = filters.languageIds.length;
      idleHint = 'None applied — search includes all languages.';
    } else {
      return null;
    }
    const { top, left, width } = dirFilterPopoverPos;
    return createPortal(
      <div
        className="provider-search-dir-popover provider-search-dir-popover-portal"
        style={{ top: `${top}px`, left: `${left}px`, width: `${width}px` }}
        role="dialog"
        aria-label={title}
      >
        <div className="provider-search-dir-popover-list" role="list">
          {options.map((opt) => (
            <label key={opt.id} className="provider-search-dir-check">
              <input type="checkbox" checked={draftIds.includes(opt.id)} onChange={() => onToggleId(opt.id)} />
              <span>{opt.label}</span>
            </label>
          ))}
        </div>
        <div className="provider-search-dir-popover-actions">
          <button type="button" className="provider-search-dir-btn" onClick={onClear}>
            Clear
          </button>
          <button type="button" className="provider-search-dir-btn provider-search-dir-btn-primary" onClick={onApply}>
            Apply
          </button>
        </div>
        <p
          className={`provider-search-dir-applied-note${appliedCount > 0 ? '' : ' provider-search-dir-applied-note-muted'}`}
        >
          {appliedCount > 0 ? `${appliedCount} applied for search.` : idleHint}
        </p>
      </div>,
      document.body
    );
  };

  const loadNeeds = async (page, append = false) => {
    if (!canViewNeeds) return;
    if (page === 1) setNeedsLoading(true);
    else setNeedsLoadingMore(true);
    try {
      const params = { page, pageSize: PAGE_SIZE };
      if (effectiveClientId) params.clientId = effectiveClientId;
      if (needsClientSearch.trim()) params.q = needsClientSearch.trim();
      if (needsTypeFilter) params.needTypeId = needsTypeFilter;
      if (needsFrequencyFilter) params.frequencyId = needsFrequencyFilter;
      if (needsServiceAreaFilter) params.serviceAreaId = needsServiceAreaFilter;
      if (needsStatusFilter && needsStatusFilter !== 'all') params.status = needsStatusFilter;
      const res = await clientServicesAPI.listNeeds(params);
      const data = res.data?.data ?? [];
      const newTotal = res.data?.total ?? 0;
      if (append) setNeeds((prev) => [...prev, ...data]);
      else setNeeds(data);
      setNeedsTotal(newTotal);
      setNeedsPage(page);
    } catch (err) {
      console.error(err);
      if (!append) setNeeds([]);
      if (!append) setNeedsTotal(0);
    } finally {
      setNeedsLoading(false);
      setNeedsLoadingMore(false);
    }
  };

  // Manager-only: stage a Delay/Change flip. Like every other field, it just marks the
  // form dirty; it persists (via its own endpoint) when the user clicks Save.
  const handleReclassify = (nextType) => {
    if (!editingNeedId) return;
    const current = editForm.issue_type === 'change' ? 'change' : 'delay';
    if (nextType === current) return;
    setEditForm((f) => ({ ...f, issue_type: nextType }));
    setEditNeedDirty(true);
    setEditFormError(null);
  };

  const needsHasMore = needs.length < needsTotal && needsTotal > 0;
  const loadMoreNeeds = useCallback(() => {
    if (!needsLoading && !needsLoadingMore && needsHasMore) loadNeeds(needsPage + 1, true);
  }, [needsLoading, needsLoadingMore, needsHasMore, needsPage]);
  useInfiniteScroll(needsSentinelRef, loadMoreNeeds, needsHasMore, needsLoading || needsLoadingMore);

  // Debounced search-as-you-type for client name
  useDebouncedSearch(needsClientSearch, () => { if (canViewNeeds && activeSection === 'needs') loadNeeds(1); }, 300);

  // Reload needs when filters, URL/override client, or tab change (not needsClientSearch - that's debounced above)
  useEffect(() => {
    if (canViewNeeds && activeSection === 'needs') loadNeeds(1);
  }, [needsTypeFilter, needsFrequencyFilter, needsServiceAreaFilter, needsStatusFilter, effectiveClientId, canViewNeeds, activeSection]);

  const loadCentralAvailability = async (page) => {
    if (!canManageCentralAvailability) return;
    setCentralLoading(true);
    try {
      const params = { page: 1, pageSize: 1000 };
      if (centralTypeFilter) params.needTypeId = centralTypeFilter;
      if (centralAreaFilter) params.serviceAreaId = centralAreaFilter;
      const res = await clientServicesAPI.listCentralAvailability(params);
      setCentralList(res.data?.data ?? []);
      setCentralTotal(res.data?.total ?? 0);
      setCentralPage(page);
    } catch (err) {
      console.error(err);
      setCentralList([]);
      setCentralTotal(0);
    } finally {
      setCentralLoading(false);
    }
  };

  useEffect(() => {
    if (canManageCentralAvailability && showCentralAvailability && activeSection === 'central') loadCentralAvailability(1);
  }, [centralTypeFilter, centralAreaFilter, canManageCentralAvailability, showCentralAvailability, activeSection]);

  const centralLookupsActive =
    showCentralAvailability &&
    canManageCentralAvailability &&
    (activeSection === 'central' || editingCentralId || showCentralAddForm);
  useEffect(() => {
    if (!centralLookupsActive) return;
    api.get('/provider-types').then((r) => setCentralProviderTypes(r.data?.data ?? r.data ?? [])).catch(() => setCentralProviderTypes([]));
    api.get('/service-areas').then((r) => setCentralServiceAreas(r.data?.data ?? r.data ?? [])).catch(() => setCentralServiceAreas([]));
  }, [centralLookupsActive]);

  /** Central add/edit: sync service type from selected provider (same pattern as SearchableProviderSelect elsewhere). */
  const applyCentralProviderSelection = useCallback((formSetter, providerIdRaw) => {
    const id = providerIdRaw ? String(providerIdRaw) : '';
    if (!id) {
      formSetter((f) => ({ ...f, provider_id: '', need_type_id: '' }));
      return;
    }
    providerAPI
      .getById(id)
      .then((res) => {
        const p = res.data?.data;
        formSetter((f) => ({
          ...f,
          provider_id: id,
          need_type_id: p?.provider_type_id != null ? String(p.provider_type_id) : f.need_type_id,
        }));
      })
      .catch(() => {
        formSetter((f) => ({ ...f, provider_id: id }));
      });
  }, []);

  const centralEditTypeLabel = useMemo(() => {
    const tid = centralEditForm.need_type_id;
    if (tid) {
      const pt = centralProviderTypes.find((p) => String(p.id) === String(tid));
      if (pt) return pt.code || pt.name || '—';
    }
    return centralEditNeedTypeSnapshot || '—';
  }, [centralEditForm.need_type_id, centralProviderTypes, centralEditNeedTypeSnapshot]);

  const centralAddTypeLabel = useMemo(() => {
    const tid = centralForm.need_type_id;
    if (!tid) return '—';
    const pt = centralProviderTypes.find((p) => String(p.id) === String(tid));
    return pt ? (pt.code || pt.name || '—') : '—';
  }, [centralForm.need_type_id, centralProviderTypes]);

  const editNeedTypeCode = useMemo(() => {
    const tid = editForm.need_type_id;
    if (!tid) return '';
    const pt = needFormProviderTypes.find((p) => String(p.id) === String(tid));
    return (pt?.code || '').trim();
  }, [editForm.need_type_id, needFormProviderTypes]);

  const centralSortedList = useMemo(() => {
    const list = [...centralList];
    const dir = centralSortDir === 'asc' ? 1 : -1;
    const by = centralSortBy;
    list.sort((a, b) => {
      let va; let vb;
      if (by === 'provider') {
        va = ([a.provider_last_name, a.provider_first_name].filter(Boolean).join(', ') + (a.payee_name || '')).toLowerCase();
        vb = ([b.provider_last_name, b.provider_first_name].filter(Boolean).join(', ') + (b.payee_name || '')).toLowerCase();
      } else if (by === 'type') {
        va = (a.need_type_code || a.need_type_name || '').toLowerCase();
        vb = (b.need_type_code || b.need_type_name || '').toLowerCase();
      } else if (by === 'service_area') {
        va = (a.service_area_name || a.service_area_code || '').toLowerCase();
        vb = (b.service_area_name || b.service_area_code || '').toLowerCase();
      } else if (by === 'ip_date') {
        va = a.ip_date || '';
        vb = b.ip_date || '';
      } else if (by === 'lvv_date') {
        va = a.lvv_date || '';
        vb = b.lvv_date || '';
      } else if (by === 'comment') {
        va = (a.provider_comment || '').toLowerCase();
        vb = (b.provider_comment || '').toLowerCase();
      } else return 0;
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
    return list;
  }, [centralList, centralSortBy, centralSortDir]);

  const displayedCentralList = useMemo(() => {
    const q = (centralProviderPayeeSearch || '').trim().toLowerCase();
    if (!q) return centralSortedList;
    return centralSortedList.filter((row) => {
      const providerPayee = ([row.provider_last_name, row.provider_first_name].filter(Boolean).join(', ') + (row.payee_name ? ` ${row.payee_name}` : '')).toLowerCase();
      return providerPayee.includes(q);
    });
  }, [centralSortedList, centralProviderPayeeSearch]);

  const availSortedNeeds = useMemo(() => {
    if (needsView !== 'availability') return needs;
    const dir = availSort.dir === 'asc' ? 1 : -1;
    return [...needs].sort((a, b) => {
      if (availSort.by === 'type') {
        const ta = (a.need_type_code || a.need_type_name || '').toLowerCase();
        const tb = (b.need_type_code || b.need_type_name || '').toLowerCase();
        return dir * ta.localeCompare(tb);
      }
      // family_availability: blanks always sort last regardless of direction
      const av = (a.family_availability || '').trim();
      const bv = (b.family_availability || '').trim();
      if (!av && !bv) return 0;
      if (!av) return 1;
      if (!bv) return -1;
      return dir * av.localeCompare(bv);
    });
  }, [needs, needsView, availSort.by, availSort.dir]);

  const handleCentralSort = (column) => {
    setCentralSortBy(column);
    setCentralSortDir((d) => (centralSortBy === column ? (d === 'asc' ? 'desc' : 'asc') : 'asc'));
  };

  const openCentralAddForm = () => {
    setCentralFormError(null);
    setEditingCentralId(null);
    setCentralSectionIpNotify(null);
    setCentralForm({ provider_id: '', service_area_id: '', need_type_id: '', ip_date: '', lvv_date: '', lvv_history: '', provider_comment: '', also_feeding: false });
    setCentralAddProviderFieldKey((k) => k + 1);
    setShowCentralAddForm(true);
  };

  const closeCentralAddForm = () => {
    setShowCentralAddForm(false);
    setCentralFormError(null);
  };

  const openCentralEdit = (row) => {
    setShowCentralAddForm(false);
    setEditingCentralId(row.id);
    setCentralFormError(null);
    setCentralSaveMessage(null);
    setCentralSectionIpNotify(null);
    setCentralEditSaveTrigger(0);
    const lvvDateInput = toDateInputValue(row.lvv_date);
    setCentralEditOriginalLvvDate(lvvDateInput);
    const typeSnap = [row.need_type_code, row.need_type_name].filter(Boolean).join(' — ');
    setCentralEditNeedTypeSnapshot(typeSnap || '');
    setCentralEditForm({
      provider_id: row.provider_id != null ? String(row.provider_id) : '',
      service_area_id: row.service_area_id != null ? String(row.service_area_id) : '',
      need_type_id: row.need_type_id != null ? String(row.need_type_id) : '',
      ip_date: toDateInputValue(row.ip_date),
      lvv_date: lvvDateInput,
      lvv_history: normalizeLvvHistoryForDisplay(row.lvv_history ?? ''),
      provider_comment: row.provider_comment ?? '',
      also_feeding: row.also_feeding === true,
    });
  };

  const closeCentralEdit = () => {
    setEditingCentralId(null);
    setCentralEditOriginalLvvDate('');
    setCentralEditNeedTypeSnapshot('');
    setCentralFormError(null);
    setCentralSaveMessage(null);
    loadCentralAvailability(centralPage);
  };

  const handleCentralAddSubmit = async (e) => {
    e.preventDefault();
    if (!centralForm.provider_id) {
      setCentralFormError('Provider is required.');
      return;
    }
    if (centralForm.ip_date && !centralForm.service_area_id) {
      setCentralFormError('Service area is required when an IP date is entered.');
      return;
    }
    let needTypeId = centralForm.need_type_id ? parseInt(String(centralForm.need_type_id), 10) : NaN;
    if (!Number.isFinite(needTypeId) && centralForm.provider_id) {
      try {
        const res = await providerAPI.getById(centralForm.provider_id);
        const pt = res.data?.data?.provider_type_id;
        if (pt != null) needTypeId = parseInt(String(pt), 10);
      } catch (_) {
        /* use needTypeId below */
      }
    }
    if (!Number.isFinite(needTypeId)) {
      setCentralFormError('Could not determine service type from provider. Select a provider and try again.');
      return;
    }
    setCentralFormSaving(true);
    setCentralFormError(null);
    try {
      const res = await clientServicesAPI.createCentralAvailability({
        provider_id: centralForm.provider_id,
        service_area_id: parseInt(centralForm.service_area_id, 10),
        need_type_id: needTypeId,
        ip_date: centralForm.ip_date || null,
        lvv_date: centralForm.lvv_date || null,
        lvv_history: centralForm.lvv_history?.trim() || null,
        provider_comment: centralForm.provider_comment?.trim() || null,
        also_feeding: centralForm.also_feeding === true,
      });
      const ipFb = centralIpNotifyFeedback(res.data?.central_availability_ip_notify);
      setCentralSectionIpNotify(ipFb);
      if (centralIpNotifyDismissRef.current) window.clearTimeout(centralIpNotifyDismissRef.current);
      if (ipFb) {
        centralIpNotifyDismissRef.current = window.setTimeout(() => {
          setCentralSectionIpNotify(null);
          centralIpNotifyDismissRef.current = null;
        }, 12000);
      }
      loadCentralAvailability(1);
      closeCentralAddForm();
    } catch (err) {
      setCentralFormError(err.response?.data?.error || err.message || 'Failed to add row');
    } finally {
      setCentralFormSaving(false);
    }
  };

  const performCentralEditSave = useCallback(async () => {
    if (!editingCentralId) return;
    if (!centralEditForm.provider_id) {
      setCentralFormError('Provider is required.');
      return;
    }
    if (centralEditForm.ip_date && !centralEditForm.service_area_id) {
      setCentralFormError('Service area is required when an IP date is entered.');
      return;
    }
    let needTypeId = centralEditForm.need_type_id ? parseInt(String(centralEditForm.need_type_id), 10) : NaN;
    if (!Number.isFinite(needTypeId) && centralEditForm.provider_id) {
      try {
        const res = await providerAPI.getById(centralEditForm.provider_id);
        const pt = res.data?.data?.provider_type_id;
        if (pt != null) needTypeId = parseInt(String(pt), 10);
      } catch (_) {
        /* use needTypeId below */
      }
    }
    if (!Number.isFinite(needTypeId)) {
      setCentralFormError('Could not determine service type from provider. Select a provider and try again.');
      return;
    }
    setCentralFormSaving(true);
    setCentralFormError(null);
    setCentralSaveMessage(null);
    try {
      const newLvvDate = centralEditForm.lvv_date?.trim() || null;
      const oldLvvDate = centralEditOriginalLvvDate?.trim() || null;
      let lvvHistoryToSave = centralEditForm.lvv_history?.trim() || null;
      if (oldLvvDate && newLvvDate !== oldLvvDate) {
        const existingHistory = centralEditForm.lvv_history?.trim() || '';
        const oldLvvDisplay = formatDisplayDate(oldLvvDate) || oldLvvDate;
        lvvHistoryToSave = existingHistory ? `${oldLvvDisplay}\n\n${existingHistory}` : oldLvvDisplay;
      }
      const res = await clientServicesAPI.updateCentralAvailability(editingCentralId, {
        provider_id: centralEditForm.provider_id,
        service_area_id: parseInt(centralEditForm.service_area_id, 10),
        need_type_id: needTypeId,
        ip_date: centralEditForm.ip_date || null,
        lvv_date: newLvvDate,
        lvv_history: lvvHistoryToSave,
        provider_comment: centralEditForm.provider_comment?.trim() || null,
        also_feeding: centralEditForm.also_feeding === true,
      });
      const ipFb = centralIpNotifyFeedback(res.data?.central_availability_ip_notify);
      setCentralSectionIpNotify(ipFb);
      if (centralIpNotifyDismissRef.current) window.clearTimeout(centralIpNotifyDismissRef.current);
      if (ipFb) {
        centralIpNotifyDismissRef.current = window.setTimeout(() => {
          setCentralSectionIpNotify(null);
          centralIpNotifyDismissRef.current = null;
        }, 12000);
      }
      setCentralSaveMessage('Saved.');
      setTimeout(() => setCentralSaveMessage(null), 2000);
      // Do not refetch list here: it can cause re-renders that interfere with typing.
      // List is refreshed when user closes the edit form.
    } catch (err) {
      setCentralFormError(err.response?.data?.error || err.message || 'Failed to update');
      throw err;
    } finally {
      setCentralFormSaving(false);
    }
  }, [editingCentralId, centralEditForm, centralEditOriginalLvvDate]);

  useAutoSave(centralEditSaveTrigger, performCentralEditSave, {
    enabled: false,
    delayMs: 800,
  });

  const handleCentralDelete = async () => {
    if (!editingCentralId || !canDeleteCentralAvailability) return;
    if (!window.confirm('Delete this central availability record?')) return;
    setCentralDeleting(true);
    setCentralFormError(null);
    try {
      await clientServicesAPI.deleteCentralAvailability(editingCentralId);
      loadCentralAvailability(centralPage);
      closeCentralEdit();
    } catch (err) {
      setCentralFormError(err.response?.data?.error || err.message || 'Failed to delete');
    } finally {
      setCentralDeleting(false);
    }
  };

  const handleOpenSendPreview = async () => {
    if (!emailByTypeNeedTypeId) {
      setEmailByTypeSendError('Select a service type first.');
      return;
    }
    setSendPreviewLoading(true);
    setEmailByTypeSendError(null);
    setEmailByTypeSendSuccess(null);
    setEmailByTypeError(null);
    setSendPreviewData(null);
    setSendPreviewOpen(true);
    try {
      const emailParams =
        emailByTypeNeedTypeId === CENTRAL_AVAILABILITY_EMAIL_ALL_FOUR_THERAPY_AGY_CTR
          ? { needTypeCode: CENTRAL_AVAILABILITY_EMAIL_ALL_FOUR_THERAPY_AGY_CTR }
          : { needTypeId: emailByTypeNeedTypeId };
      const res = await clientServicesAPI.prepareCentralAvailabilityEmail(emailParams);
      const data = res.data?.data ?? res.data ?? {};
      const count = data.recipientCount ?? (data.recipientEmails?.length ?? 0);
      if (count === 0) {
        setEmailByTypeSendError(
          emailByTypeNeedTypeId === CENTRAL_AVAILABILITY_EMAIL_ALL_FOUR_THERAPY_AGY_CTR
            ? 'No payee emails for Agency/Center (AGY/CTR) providers. Add providers with those types and payees that have email addresses.'
            : 'No provider emails for this type. Add providers of this type first.'
        );
        setSendPreviewOpen(false);
        setSendPreviewLoading(false);
        return;
      }
      setSendPreviewData({
        subject: data.subject || '(No subject)',
        body: data.body || '',
        recipientCount: count,
        recipientEmails: data.recipientEmails || [],
      });
    } catch (err) {
      setEmailByTypeSendError(err.response?.data?.error || err.message || 'Failed to prepare preview');
      setSendPreviewOpen(false);
    } finally {
      setSendPreviewLoading(false);
    }
  };

  const handleConfirmSendEmail = () => {
    if (!sendPreviewData) return;
    const subject = sendPreviewData.subject || '';
    const body = sendPreviewData.body || '';
    const qs = `subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    openMailtoPreferNewTab(`mailto:?${qs}`);
  };

  const handleCopyBccList = () => {
    const emails = (sendPreviewData?.recipientEmails || []).join('; ');
    navigator.clipboard.writeText(emails)
      .then(() => alert(`BCC list copied (${sendPreviewData?.recipientEmails?.length ?? 0} addresses). Paste it into the BCC field in Outlook.`))
      .catch(() => alert('Could not copy to clipboard.'));
  };

  const handleProvenSearchProviders = async () => {
    if (!canSearchProviders) return;
    if (!provenNeedTypeIds.length || !provenServiceAreaIds.length) {
      setProvenError('Select at least one service type and one service area.');
      setProvenHasSearched(true);
      setProvenResults([]);
      return;
    }
    setProvenError(null);
    setProvenHasSearched(true);
    setProvenSearching(true);
    try {
      const params = {
        needTypeIds: provenNeedTypeIds.join(','),
        serviceAreaIds: provenServiceAreaIds.join(','),
      };
      const res = await clientServicesAPI.searchProvenProvidersFromNeeds(params);
      const rows = res.data?.data ?? res.data ?? [];
      setProvenResults(Array.isArray(rows) ? rows : []);
    } catch (err) {
      console.error('Proven-needs provider search failed:', err);
      setProvenError(err.response?.data?.error || err.message || 'Search failed');
      setProvenResults([]);
    } finally {
      setProvenSearching(false);
    }
  };

  const handleProvenCopyEmails = () => {
    const emails = provenResults.map((p) => p.email).filter(Boolean);
    const uniq = [...new Set(emails)];
    const text = uniq.join('; ');
    if (!text) {
      alert('No provider emails in the result set.');
      return;
    }
    navigator.clipboard.writeText(text).then(() => alert('Provider emails copied to clipboard.')).catch(() => alert('Could not copy.'));
  };

  const handleSearchProviders = async () => {
    if (!canSearchProviders) return;
    setSearchError(null);
    setHasSearched(true);
    setSearching(true);
    try {
      const params = {};
      const allProviderTypeIds = providerTypes.map((pt) => String(pt.id));
      const providerTypeIdsToSend =
        filters.providerTypeIds && filters.providerTypeIds.length ? filters.providerTypeIds : allProviderTypeIds;
      if (providerTypeIdsToSend.length) params.providerTypeIds = providerTypeIdsToSend.join(',');
      if (filters.serviceAreaIds && filters.serviceAreaIds.length) params.serviceAreaIds = filters.serviceAreaIds.join(',');
      if (filters.credentialIds && filters.credentialIds.length) params.credentialIds = filters.credentialIds.join(',');
      if (filters.languageIds && filters.languageIds.length) params.languageIds = filters.languageIds.join(',');
      if (filters.nonAssistant) params.nonAssistant = 'true';
      const res = await clientServicesAPI.searchProviders(params);
      const rows = res.data?.data ?? res.data ?? [];
      setSearchResults(Array.isArray(rows) ? rows : []);
    } catch (err) {
      console.error('Provider search failed:', err);
      const message = err.response?.data?.error || err.message || 'Search failed';
      setSearchError(message);
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  const handleCopyBcc = () => {
    setActionsDropdownOpen(false);
    const emails = searchResults.map((p) => p.email).filter(Boolean);
    const uniq = [...new Set(emails)];
    navigator.clipboard.writeText(uniq.join('; ')).then(() => alert('BCC list copied to clipboard.')).catch(() => alert('Could not copy.'));
  };

  const handleMailto = () => {
    setActionsDropdownOpen(false);
    const emails = searchResults.map((p) => p.email).filter(Boolean);
    const uniq = [...new Set(emails)];
    openMailtoPreferNewTab(`mailto:?bcc=${encodeURIComponent(uniq.join(';'))}`);
  };

  const resultsTotal = searchResults.length;

  useEffect(() => {
    if (!actionsDropdownOpen) return;
    const handleClickOutside = (e) => {
      if (actionsDropdownRef.current && !actionsDropdownRef.current.contains(e.target)) setActionsDropdownOpen(false);
    };
    document.addEventListener('pointerdown', handleClickOutside);
    return () => document.removeEventListener('pointerdown', handleClickOutside);
  }, [actionsDropdownOpen]);

  const handleExportCsv = () => {
    setActionsDropdownOpen(false);
    const filterServiceAreas = directoryFilterExportLabels(filters.serviceAreaIds, directoryServiceAreaOptions);
    const filterCredentials = directoryFilterExportLabels(filters.credentialIds, directoryCredentialOptions);
    const filterLanguages = directoryFilterExportLabels(filters.languageIds, directoryLanguageOptions);
    const headers = [
      'Last Name',
      'First Name',
      'Provider email',
      'Payee email',
      'Payee',
      'Type',
      'Search filter: Service areas',
      'Search filter: Credentials',
      'Search filter: Languages',
    ];
    const rows = searchResults.map((p) => [
      p.last_name || '',
      p.first_name || '',
      p.email || '',
      p.payee_email || '',
      p.payee_name || '',
      p.provider_type_name || p.provider_type_code || '',
      filterServiceAreas,
      filterCredentials,
      filterLanguages,
    ]);
    const csv = [headers.join(','), ...rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'provider-search-results.csv';
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const openAddForm = () => {
    setAddFormError(null);
    if (effectiveClientId) {
      setNeedForm((f) => ({ ...f, client_id: effectiveClientId, referral_id: effectiveReferralId || (referrals[0] ? String(referrals[0].id) : '') }));
    } else {
      setNeedForm(emptyNeedForm());
      setReferrals([]);
    }
    setShowAddForm(true);
  };

  const closeAddForm = () => {
    setShowAddForm(false);
    setNeedForm(emptyNeedForm());
    setAddFormError(null);
  };

  const openEditForm = (need) => {
    needActualProviderExplicitClearRef.current = false;
    setNeedCloseDateHintEditOpen(false);
    setEditingNeedId(need.id);
    setDelayInfoOpen(!!need.delay_start_date);
    setEditFormError(null);
    setEditFormSaveMessage(null);
    setEditNeedDirty(false);
    const fromRow =
      [need.client_last_name, need.client_first_name].filter(Boolean).join(', ') ||
      (need.client_id ? `Client ${need.client_id}` : '');
    setEditFormClientName(fromRow || clientNameFromUrl || '');
    setEditFormClientDob(need.client_dob || need.date_of_birth || '');
    setEditFormAssignedScName(need.client_assigned_sc_name || need.clientAssignedScName || need.assigned_sc_name || need.assignedScName || '');
  };

  const closeEditForm = () => {
    needActualProviderExplicitClearRef.current = false;
    setEditNeedDirty(false);
    setEditingNeedId(null);
    setEditFormError(null);
    setEditFormSaveMessage(null);
    setCommitMatchSuccess(null);
    setEditFormClientName('');
    setEditFormAssignedScName('');
    setNeedReferralId(null);
    setShowMatchList(false);
    setMatchList([]);
    setMatchListError(null);
    setCommitMatchError(null);
    setExpandedCommentRowId(null);
    setMatchContactOpenRowId(null);
    setNeedCloseDateHintEditOpen(false);
  };

  const fetchMatchList = useCallback(async () => {
    if (!editForm.need_type_id) {
      setMatchListError('Set service type first.');
      return;
    }
    setMatchListLoading(true);
    setMatchListError(null);
    setCommitMatchError(null);
    setMatchContactOpenRowId(null);
    setShowMatchList(true);
    try {
      const res = await clientServicesAPI.matchCentralAvailability({
        needTypeId: editForm.need_type_id,
        serviceAreaId: editForm.service_area_id || undefined,
        needFeeding: editForm.need_feeding === true,
      });
      const rows = res.data?.data ?? res.data ?? [];
      setMatchList(Array.isArray(rows) ? rows : []);
    } catch (err) {
      setMatchListError(err.response?.data?.error || err.message || 'Failed to load matches.');
      setMatchList([]);
    } finally {
      setMatchListLoading(false);
    }
  }, [editForm.need_type_id, editForm.service_area_id, editForm.need_feeding]);

  const handleCommitMatch = useCallback(async (row, teamOptions = {}) => {
    const { service_delivery_mode, service_delivery_start_date } = teamOptions;
    if (!editingNeedId || !row?.provider_id || !needReferralId) return;
    if (!service_delivery_mode || !['IN_PERSON', 'LVV', 'BOTH'].includes(service_delivery_mode)) {
      setCommitMatchError('Please select how the service will be delivered.');
      return;
    }
    const deliveryStartDate = (service_delivery_start_date || '').trim();
    if (!deliveryStartDate) {
      setCommitMatchError('A delivery start date is required when you match a provider.');
      return;
    }
    // The need's Close date is the day the provider was matched (today) — even when delivery
    // starts later. The delivery start date (which may be in the future) is recorded separately.
    const _cd = new Date();
    const matchCloseDate = `${_cd.getFullYear()}-${String(_cd.getMonth() + 1).padStart(2, '0')}-${String(_cd.getDate()).padStart(2, '0')}`;
    setCommittingMatch(true);
    setCommitMatchError(null);
    setCommitMatchSuccess(null);
    const providerLabel = [row.provider_last_name, row.provider_first_name].filter(Boolean).join(', ') || 'Provider';
    const modeLabel = service_delivery_mode === 'LVV' ? 'Live Video Visit (LVV)' : service_delivery_mode === 'BOTH' ? 'Both: In Person / LVV' : 'In Person';
    try {
      await clientServicesAPI.updateNeed(editingNeedId, {
        actual_provider_id: row.provider_id,
        actual_provider_office_value: `provider:${row.provider_id}`,
        need_close: matchCloseDate,
        service_delivery_start_date: deliveryStartDate,
        service_delivery_mode,
        status: 'matched',
      });
      const frequencyId = editForm.frequency_id ? parseInt(editForm.frequency_id, 10) : null;
      const teamPayload = {
        provider_id: row.provider_id,
        service_delivery_mode,
        frequency_id: frequencyId,
        service_delivery_start_date: deliveryStartDate,
      };
      try {
        await clientProviderServiceAPI.createForReferral(needReferralId, teamPayload);
      } catch (addErr) {
        if (addErr.response?.status === 409) {
          // Backend auto-add already inserted the row — update it with service_delivery_mode + dates.
          try {
            const teamRes = await clientProviderServiceAPI.getByReferralId(needReferralId);
            const existing = (teamRes.data?.data || []).find(
              (e) => String(e.provider_id) === String(row.provider_id)
            );
            if (existing?.id) {
              await clientProviderServiceAPI.update(existing.id, {
                service_delivery_mode,
                frequency_id: frequencyId,
                service_delivery_start_date: deliveryStartDate,
              });
            }
          } catch (updateErr) {
            console.warn('Could not update team entry with service delivery mode:', updateErr.message);
          }
        } else {
          throw addErr;
        }
      }
      setEditForm((f) => ({
        ...f,
        actual_provider_id: String(row.provider_id),
        actual_provider_office_value: `provider:${row.provider_id}`,
        actualProviderOfficeDisplayLabel: formatProviderDisplayLabel({
          first_name: row.provider_first_name,
          last_name: row.provider_last_name,
          payee_name: row.payee_name,
          provider_type_code: row.need_type_code,
          id: row.provider_id,
          provider_id: row.provider_id,
        }),
        need_close: matchCloseDate,
        service_delivery_start_date: deliveryStartDate,
        service_delivery_mode,
        status: 'matched',
      }));
      setShowMatchList(false);
      setMatchList([]);
      setMatchDialogRow(null);
      setCommitMatchSuccess(`${providerLabel} has been added to the client's Provider Team (${modeLabel}). The delivery start date and frequency were copied to the team, and the need was closed as of today. You can view the team under Client → Provider Team.`);
      loadNeeds(1);
    } catch (err) {
      setCommitMatchError(err.response?.data?.error || err.message || 'Failed to save provider selection.');
    } finally {
      setCommittingMatch(false);
    }
  }, [editingNeedId, needReferralId, editForm.frequency_id]);

  const performNeedEditSave = useCallback(async () => {
    const form = editFormRef.current;
    if (!editingNeedId) return;
    if (!form.need_type_id || !form.need_start) {
      setEditFormError('Service type and start date are required.');
      return;
    }
    const isDgn = needTypeIdIsDgn(needFormProviderTypes, form.need_type_id);
    if (isDgn) {
      if (!form.diagnostic_reason || !String(form.diagnostic_reason).trim()) {
        setEditFormError('Diagnostic reason is required for DGN service needs.');
        return;
      }
      if (form.diagnostic_reason === 'autism' && form.autism_family_discussed === null) {
        setEditFormError('Please indicate whether the possibility of autism has been discussed with the family.');
        return;
      }
    } else if (!form.frequency_id) {
      setEditFormError('Frequency is required.');
      return;
    }
    // Service area is required for all need types except DGN.
    if (!isDgn && (form.service_area_id == null || String(form.service_area_id).trim() === '')) {
      setEditFormError('Service area is required.');
      return;
    }
    // A delay reason is required once a delay start date is entered — unless the manager has
    // reclassified this need as a "change" (a change is not a delay and needs no delay reason).
    if (form.issue_type !== 'change' &&
        form.delay_start_date && String(form.delay_start_date).trim() !== '' &&
        (form.delay_reason_id == null || String(form.delay_reason_id).trim() === '')) {
      setEditFormError('Delay reason is required when a delay start date is entered.');
      return;
    }
    // Matching a provider requires a close (match) date (non-DGN).
    const hasProviderPick = (form.actual_provider_office_value || '').trim() !== '' || (form.actual_provider_id || '').trim() !== '';
    if (!isDgn && hasProviderPick && (!form.need_close || String(form.need_close).trim() === '')) {
      setEditFormError('A match date (Close date) is required when you match a provider.');
      return;
    }
    setEditFormSaving(true);
    setEditFormError(null);
    setEditFormSaveMessage(null);
    const payload = {
      need_type_id: parseInt(form.need_type_id, 10),
      service_area_id: form.service_area_id ? parseInt(form.service_area_id, 10) : null,
      need_start: form.need_start,
      frequency_id: form.frequency_id ? parseInt(form.frequency_id, 10) : null,
      status: form.status,
      need_close: form.need_close.trim() || null,
      need_feeding: form.need_feeding === true,
      sc_note: form.sc_note.trim() || null,
      family_availability: form.family_availability.trim() || null,
      service_delivery_mode: form.service_delivery_mode || null,
      service_delivery_start_date: form.service_delivery_start_date || null,
      delay_start_date: form.delay_start_date || null,
      delay_reason_id: form.delay_reason_id ? parseInt(form.delay_reason_id, 10) : null,
      delay_end_date: form.delay_end_date || null,
      delay_reported_to_dhs_date: form.delay_reported_to_dhs_date || null,
      delay_comment: (form.delay_comment || '').trim() || null,
      // Not persisted by the need-update whitelist (issue_type is written via reclassifyNeed),
      // but included so the backend's delay-reason check knows a "change" needs no delay reason.
      issue_type: form.issue_type === 'change' ? 'change' : (form.issue_type === 'delay' ? 'delay' : null),
    };
    if (needTypeIdIsDgn(needFormProviderTypes, form.need_type_id)) {
      payload.diagnostic_reason = form.diagnostic_reason
        ? String(form.diagnostic_reason).trim().toLowerCase()
        : null;
      if (form.diagnostic_reason === 'autism' && form.autism_family_discussed !== null) {
        payload.autism_family_discussed = form.autism_family_discussed;
      }
    }
    let providerPayloadIncluded = false;
    if (needActualProviderExplicitClearRef.current) {
      needActualProviderExplicitClearRef.current = false;
      payload.actual_provider_office_value = null;
      payload.actual_provider_id = null;
      providerPayloadIncluded = true;
    } else {
      const officeVal = (form.actual_provider_office_value || '').trim();
      const provVal = (form.actual_provider_id || '').trim();
      if (officeVal !== '') {
        payload.actual_provider_office_value = officeVal;
        if (provVal) payload.actual_provider_id = provVal;
        providerPayloadIncluded = true;
      } else if (provVal !== '') {
        payload.actual_provider_office_value = `provider:${provVal}`;
        payload.actual_provider_id = provVal;
        providerPayloadIncluded = true;
      }
    }
    const sentOfficePayload = Object.prototype.hasOwnProperty.call(payload, 'actual_provider_office_value')
      ? payload.actual_provider_office_value
      : undefined;
    try {
      const res = await clientServicesAPI.updateNeed(editingNeedId, payload);
      let merged = res.data?.data ?? res.data;
      if (
        merged &&
        merged.id !== undefined &&
        merged.id !== null &&
        merged.id !== '' &&
        sentOfficePayload != null &&
        String(sentOfficePayload).trim() !== '' &&
        !actualProviderOfficeValueFromNeed(merged)
      ) {
        try {
          const freshRes = await clientServicesAPI.getNeed(editingNeedId);
          const fresh = freshRes.data?.data ?? freshRes.data;
          if (fresh && fresh.id != null) merged = fresh;
        } catch (_) {
          /* keep PATCH body */
        }
      }
      if (merged && merged.id !== undefined && merged.id !== null && merged.id !== '') {
        const fromServer = needToActualProviderOfficeFormFields(merged);
        setEditForm((f) => {
          const localOffice = (f.actual_provider_office_value || '').trim();
          const localLabel = (f.actualProviderOfficeDisplayLabel || '').trim();
          const localProv = (f.actual_provider_id || '').trim();
          const serverOffice = (fromServer.actual_provider_office_value || '').trim();
          const serverProv = (fromServer.actual_provider_id || '').trim();
          const hadLocalPick = localOffice !== '' || localLabel !== '' || localProv !== '';
          const serverHasPick = serverOffice !== '' || serverProv !== '';
          if (hadLocalPick && !serverHasPick) {
            return {
              ...f,
              ...fromServer,
              actual_provider_office_value: f.actual_provider_office_value,
              actualProviderOfficeDisplayLabel: f.actualProviderOfficeDisplayLabel,
              actual_provider_id: f.actual_provider_id,
            };
          }
          const sentNonEmpty =
            sentOfficePayload != null && String(sentOfficePayload).trim() !== '';
          if (sentNonEmpty && !serverOffice) {
            return {
              ...f,
              ...fromServer,
              actual_provider_office_value: String(sentOfficePayload).trim(),
              actualProviderOfficeDisplayLabel:
                f.actualProviderOfficeDisplayLabel || form.actualProviderOfficeDisplayLabel,
              actual_provider_id: f.actual_provider_id || form.actual_provider_id,
            };
          }
          return { ...f, ...fromServer };
        });
      }
      // Manager-only Delay/Change reclassification: issue_type is not part of the normal
      // need-update whitelist, so persist it here (only when changed) via its own endpoint.
      if (canReclassifyNeed) {
        const desired = form.issue_type === 'change' ? 'change' : (form.issue_type === 'delay' ? 'delay' : null);
        const serverIssueType = merged && merged.issue_type ? merged.issue_type : null;
        if (desired && desired !== serverIssueType) {
          await clientServicesAPI.reclassifyNeed(editingNeedId, desired);
        }
      }
      loadNeeds(1);
      setEditNeedDirty(false);
      const dgnNotify = res.data?.dgn_sc_notify;
      const dgnLine = dgnNotify?.status === 'sent'
        ? "Internal notification sent to the service coordinator's inbox."
        : dgnNotify?.status === 'skipped' && dgnNotify?.reason
          ? `Notification not sent: ${dgnNotify.reason}`
          : null;
      // When this save newly matched a provider, the backend also placed them on the Provider Team.
      const teamAdded = res.data?.provider_team_added === true;
      const providerLabel = (editFormRef.current.actualProviderOfficeDisplayLabel || '').trim();
      const teamLine = teamAdded
        ? `${providerLabel ? `${providerLabel} has` : 'The provider has'} been added to the client's Provider Team. You can view the team under Client → Provider Team.`
        : null;
      const extraLine = [dgnLine, teamLine].filter(Boolean).join(' ');
      setEditFormSaveMessage(extraLine ? `Saved. ${extraLine}` : 'Saved.');
      setTimeout(() => setEditFormSaveMessage(null), extraLine ? 5000 : 2000);
    } catch (err) {
      setEditFormError(err.response?.data?.error || err.message || 'Failed to update service');
      throw err;
    } finally {
      setEditFormSaving(false);
    }
  }, [editingNeedId, needsPage, needFormProviderTypes, canReclassifyNeed]);

  const handleEditNeedSave = useCallback(async () => {
    try {
      await performNeedEditSave();
    } catch (_) {
      /* performNeedEditSave sets editFormError */
    }
  }, [performNeedEditSave]);

  const handleEditNeedCancel = () => {
    if (editNeedDirty) {
      const ok = window.confirm('Discard unsaved changes to this need?');
      if (!ok) return;
    }
    closeEditForm();
  };

  const handleAddNeedSubmit = async (e) => {
    e.preventDefault();
    // Use URL/referrals when in client context so submit works even if form state wasn't synced yet
    const formClientId = needForm.client_id || effectiveClientId || '';
    const formReferralId = needForm.referral_id || effectiveReferralId || (referrals.length > 0 ? String(referrals[0].id) : '');
    if (!formClientId || !formReferralId || !needForm.need_type_id || !needForm.need_start) {
      if (formClientId && !formReferralId) {
        setAddFormError('No referral found for this client. Service needs require a referral.');
      } else {
        setAddFormError('Client, service type, and start date are required. (Referral is set to the current referral.)');
      }
      return;
    }
    const addIsDgn = needTypeIdIsDgn(needFormProviderTypes, needForm.need_type_id);
    if (addIsDgn) {
      if (!needForm.diagnostic_reason || !String(needForm.diagnostic_reason).trim()) {
        setAddFormError('Diagnostic reason is required for DGN service needs.');
        return;
      }
      if (needForm.diagnostic_reason === 'autism' && needForm.autism_family_discussed === null) {
        setAddFormError('Please indicate whether the possibility of autism has been discussed with the family.');
        return;
      }
    } else if (!needForm.frequency_id) {
      setAddFormError('Frequency is required.');
      return;
    }
    // Service area is required for all need types except DGN.
    if (!addIsDgn && (needForm.service_area_id == null || String(needForm.service_area_id).trim() === '')) {
      setAddFormError('Service area is required.');
      return;
    }
    setAddFormSaving(true);
    setAddFormError(null);
    try {
      const createRes = await clientServicesAPI.createNeed({
        client_id: formClientId,
        referral_id: formReferralId,
        need_type_id: parseInt(needForm.need_type_id, 10),
        service_area_id: needForm.service_area_id ? parseInt(needForm.service_area_id, 10) : null,
        need_start: needForm.need_start,
        frequency_id: needForm.frequency_id ? parseInt(needForm.frequency_id, 10) : null,
        need_feeding: needForm.need_feeding === true,
        sc_note: needForm.sc_note.trim() || null,
        family_availability: needForm.family_availability.trim() || null,
        status: 'open',
        ...(needTypeIdIsDgn(needFormProviderTypes, needForm.need_type_id)
          ? {
            diagnostic_reason: String(needForm.diagnostic_reason).trim().toLowerCase(),
            ...(needForm.diagnostic_reason === 'autism' && needForm.autism_family_discussed !== null
              ? { autism_family_discussed: needForm.autism_family_discussed }
              : {}),
          }
          : {}),
      });
      // Offer to add a "Waiting <discipline>" placeholder to the Provider Team (all but DGN).
      const createdNeed = createRes?.data?.data ?? createRes?.data;
      const createdNeedId = createdNeed?.id ?? null;
      if (!addIsDgn && createdNeedId) {
        const disciplineLabel = (needFormProviderTypes.find((pt) => String(pt.id) === String(needForm.need_type_id))?.code) || 'this discipline';
        const wantWaiting = window.confirm(
          `Add a "Waiting ${disciplineLabel}" placeholder to the Provider Team for this need?\n\nThis keeps the team showing the unmatched service; it is replaced automatically when you match a provider.`
        );
        if (wantWaiting) {
          try {
            await clientServicesAPI.addWaitingToTeam(createdNeedId);
          } catch (waitErr) {
            // Non-fatal: the need was created; surface a soft note only.
            console.warn('Add Waiting placeholder failed:', waitErr.response?.data?.error || waitErr.message);
          }
        }
      }
      loadNeeds(1);
      closeAddForm();
      setSearchParams({}); // clear clientId/referralId from URL after adding
    } catch (err) {
      setAddFormError(err.response?.data?.error || err.message || 'Failed to create service');
    } finally {
      setAddFormSaving(false);
    }
  };

  const openAddFormFromHeader = () => {
    setActiveSection('needs');
    setTimeout(() => openAddForm(), 0);
  };

  const navSections = [
    { id: 'needs', label: 'Needs' },
    { id: 'search', label: 'Search Providers' },
    ...(showCentralAvailability && canManageCentralAvailability && !isServiceCoordinator && !effectiveClientId ? [{ id: 'central', label: 'Central Availability' }] : []),
  ];

  return (
    <div className="provider-search-page">
      <div className="provider-search-header">
        <PageHeader title="Client Service Needs">
          {canManageNeeds && activeSection === 'needs' && (
            <button type="button" className="btn-new" onClick={openAddFormFromHeader}>
              + Add need
            </button>
          )}
        </PageHeader>
      </div>

      <div className="provider-search-layout">
        <div
          className={`provider-search-nav-panel side-nav-layout-nav--collapse-rail ${sectionsNavCollapsed ? 'side-nav-layout-nav--collapsed' : ''}`}
        >
          <div className="side-nav-layout-nav-with-rail">
            <div className="side-nav-layout-nav-inner">
              <div className="provider-search-nav-inner">
                <nav className="provider-search-nav" aria-label="Client service sections">
                  <h2 className="provider-search-nav-title">Sections</h2>
                  <ul className="provider-search-nav-list">
                    {navSections.map(({ id, label }) => (
                      <li key={id}>
                        <button
                          type="button"
                          className={`provider-search-nav-item ${activeSection === id ? 'active' : ''}`}
                          onClick={() => setActiveSection(id)}
                        >
                          {label}
                        </button>
                      </li>
                    ))}
                  </ul>
                </nav>
              </div>
            </div>
            <div className="side-nav-layout-collapse-rail-strip">
              <button
                type="button"
                className="side-nav-layout-collapse-rail-btn no-print"
                onClick={toggleSectionsNav}
                aria-expanded={!sectionsNavCollapsed}
                title={sectionsNavCollapsed ? 'Show sections' : 'Hide sections'}
                aria-label={sectionsNavCollapsed ? 'Expand sections panel' : 'Collapse sections panel'}
              >
                <svg className="side-nav-layout-collapse-rail-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  {sectionsNavCollapsed ? (
                    <path d="M9 6L15 12L9 18" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                  ) : (
                    <path d="M15 6L9 12L15 18" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                  )}
                </svg>
              </button>
            </div>
          </div>
        </div>

        <div className="provider-search-content">
          {activeSection === 'needs' && (
            <>
        <section className="provider-search-needs">
          {showAddForm && canManageNeeds && (
            <div className="provider-search-add-form">
              <h3>Add need</h3>
              {addFormError && <div className="provider-search-form-error">{addFormError}</div>}
              <form onSubmit={handleAddNeedSubmit}>
                <div className="provider-search-form-grid">
                  {effectiveClientId ? (
                    <label>
                      Client
                      <input type="text" value={clientNameFromUrl || `Client ${effectiveClientId}`} readOnly disabled className="provider-search-input-readonly" />
                      <input type="hidden" value={effectiveClientId} readOnly />
                    </label>
                  ) : (
                    <label>
                      Client *
                      <select
                        value={needForm.client_id}
                        onChange={(e) => onNeedFormClientChange(e.target.value)}
                        required
                      >
                        <option value="">Select client…</option>
                        {clients.map((c) => (
                          <option key={c.id} value={c.id}>{[c.last_name, c.first_name].filter(Boolean).join(', ') || `Client ${c.id}`}</option>
                        ))}
                      </select>
                    </label>
                  )}
                  <label>
                    Service type *
                    <select
                      value={needForm.need_type_id}
                      onChange={(e) => {
                        const v = e.target.value;
                        setNeedForm((f) => {
                          const next = { ...f, need_type_id: v };
                          if (!needTypeIdIsDgn(needFormProviderTypes, v)) {
                            next.diagnostic_reason = '';
                            next.autism_family_discussed = null;
                          }
                          return next;
                        });
                      }}
                      required
                    >
                      <option value="">Select type…</option>
                      {needFormProviderTypes.filter(isClientServiceNeedType).map((pt) => (
                        <option key={pt.id} value={pt.id}>{pt.code || pt.name}</option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Service area{!addFormNeedIsDgn ? ' *' : ''}
                    <select
                      value={needForm.service_area_id}
                      onChange={(e) => setNeedForm((f) => ({ ...f, service_area_id: e.target.value }))}
                      required={!addFormNeedIsDgn}
                    >
                      <option value="">{addFormNeedIsDgn ? 'Optional' : 'Select…'}</option>
                      {needFormServiceAreas.map((sa) => (
                        <option key={sa.id} value={sa.id}>{sa.name || sa.code}</option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Date of Consent *
                    <input
                      type="date"
                      value={needForm.need_start}
                      onChange={(e) => setNeedForm((f) => ({ ...f, need_start: e.target.value }))}
                      required
                    />
                  </label>
                  {addFormNeedIsDgn && (
                    <>
                      <label>
                        Diagnostic reason *
                        <select
                          value={needForm.diagnostic_reason}
                          onChange={(e) => setNeedForm((f) => ({
                            ...f,
                            diagnostic_reason: e.target.value,
                            autism_family_discussed: e.target.value !== 'autism' ? null : f.autism_family_discussed,
                          }))}
                          required
                        >
                          <option value="">Select…</option>
                          {DGN_DIAGNOSTIC_REASON_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </select>
                      </label>
                      {needForm.diagnostic_reason === 'autism' && (
                        <div className="provider-search-autism-discussion">
                          <p className="provider-search-autism-discussion-label">Has the possibility of autism been discussed with the family? *</p>
                          <div className="provider-search-autism-discussion-options">
                            <label className="provider-search-autism-discussion-option">
                              <input
                                type="radio"
                                name="autism_family_discussed_add"
                                checked={needForm.autism_family_discussed === true}
                                onChange={() => setNeedForm((f) => ({ ...f, autism_family_discussed: true }))}
                              />
                              Yes
                            </label>
                            <label className="provider-search-autism-discussion-option">
                              <input
                                type="radio"
                                name="autism_family_discussed_add"
                                checked={needForm.autism_family_discussed === false}
                                onChange={() => setNeedForm((f) => ({ ...f, autism_family_discussed: false }))}
                              />
                              No
                            </label>
                          </div>
                          {needForm.autism_family_discussed === false && (
                            <p className="provider-search-autism-discussion-advisory">
                              Please add context in the SC Note field about why this has not yet been discussed.
                            </p>
                          )}
                        </div>
                      )}
                    </>
                  )}
                  {!addFormNeedIsDgn && (
                  <label>
                    Frequency *
                    <select
                      value={needForm.frequency_id}
                      onChange={(e) => setNeedForm((f) => ({ ...f, frequency_id: e.target.value }))}
                      required
                    >
                      <option value="">Select frequency…</option>
                      {frequencies.map((f) => (
                        <option key={f.id} value={f.id}>{f.description || f.code}</option>
                      ))}
                    </select>
                  </label>
                  )}
                  {!addFormNeedIsDgn && (
                  <label className="provider-search-form-check">
                    <input
                      type="checkbox"
                      checked={needForm.need_feeding === true}
                      onChange={(e) => setNeedForm((f) => ({ ...f, need_feeding: e.target.checked }))}
                    />
                    <span>Feeding</span>
                  </label>
                  )}
                </div>
                {!addFormNeedIsDgn && (
                <label className="provider-search-form-note provider-search-family-availability-label">
                  <span className="provider-search-label-with-info">
                    Family availability
                    <button
                      type="button"
                      className="provider-search-info-icon"
                      onClick={(e) => { e.preventDefault(); setFamilyAvailabilityHintOpen((v) => !v); }}
                      aria-expanded={familyAvailabilityHintOpen}
                      aria-controls="provider-search-family-availability-info"
                      aria-label="Toggle family availability info"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/></svg>
                    </button>
                  </span>
                  {familyAvailabilityHintOpen && (
                    <p className="provider-search-info-text" id="provider-search-family-availability-info">
                      Do NOT include PII. Do NOT include Method. (Providers see this field.)
                    </p>
                  )}
                  <input
                    type="text"
                    className="provider-search-family-availability-input"
                    value={needForm.family_availability}
                    onChange={(e) => setNeedForm((f) => ({ ...f, family_availability: e.target.value }))}
                    placeholder="Optional"
                  />
                </label>
                )}
                <label className="provider-search-form-note">
                  <span className="provider-search-label-with-info">
                    SC note
                    <InfoIconButton
                      onClick={(e) => { e.preventDefault(); setScNoteHintOpen((v) => !v); }}
                      ariaExpanded={scNoteHintOpen}
                      ariaControls="provider-search-sc-note-info-add"
                      ariaLabel="Show SC note info"
                      ariaLabelExpanded="Hide SC note info"
                    />
                  </span>
                  {scNoteHintOpen && (
                    <p className="provider-search-info-text" id="provider-search-sc-note-info-add">
                      Family preferences for method and other notes about providers not to use, etc. (This is just for you.)
                    </p>
                  )}
                  <textarea
                    value={needForm.sc_note}
                    onChange={(e) => setNeedForm((f) => ({ ...f, sc_note: e.target.value }))}
                    rows={2}
                    placeholder="Optional coordination notes"
                  />
                </label>
                <div className="provider-search-form-actions">
                  <button type="button" className="btn-cancel" onClick={closeAddForm} disabled={addFormSaving}>Cancel</button>
                  <button type="submit" className="btn-primary" disabled={addFormSaving}>{addFormSaving ? 'Saving…' : 'Save need'}</button>
                </div>
              </form>
            </div>
          )}
          {editingNeedId && canViewNeeds && (
            <div className="provider-search-add-form provider-search-edit-form">
              <div className="provider-search-edit-form-header">
                <div className="provider-search-edit-form-title-row">
                  <h3>Edit Need</h3>
                  <div className="provider-search-edit-form-header-actions">
                    {showCentralAvailability && editForm.need_type_id && (
                      <button
                        type="button"
                        className="btn-primary provider-search-match-find-btn"
                        onClick={fetchMatchList}
                        disabled={matchListLoading}
                      >
                        {matchListLoading ? 'Finding…' : 'Find matches'}
                      </button>
                    )}
                    {!editFormLoading && (
                      <>
                        <button type="button" className="btn-cancel" onClick={handleEditNeedCancel} disabled={editFormSaving}>
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="btn-save"
                          disabled={!canManageNeeds || !editNeedDirty || editFormSaving}
                          onClick={handleEditNeedSave}
                        >
                          {editFormSaving ? 'Saving…' : 'Save'}
                        </button>
                      </>
                    )}
                  </div>
                </div>
                {editFormClientName ? (
                  <p className="provider-search-edit-form-client-heading" aria-live="polite">
                    {editFormClientName}
                    {editFormClientDob ? <span className="provider-search-edit-form-client-dob">  ·  DOB: {formatDisplayDate(editFormClientDob)}</span> : null}
                  </p>
                ) : null}
                {(editFormError || editFormSaveMessage || editFormSaving || commitMatchSuccess) && (
                  <div className="provider-search-central-edit-status">
                    {editFormSaving && <span className="provider-search-central-status-saving">Saving…</span>}
                    {editFormSaveMessage && !editFormSaving && <span className="provider-search-central-status-saved">{editFormSaveMessage}</span>}
                    {editFormError && <span className="provider-search-form-error">{editFormError}</span>}
                    {commitMatchSuccess && <span className="provider-search-commit-match-success">{commitMatchSuccess}</span>}
                  </div>
                )}
              </div>
              {showCentralAvailability && editForm.need_type_id && showMatchList && (
                <div className="provider-search-match-list-wrap">
                  {matchListError && <p className="provider-search-form-error">{matchListError}</p>}
                  {commitMatchError && <p className="provider-search-form-error">{commitMatchError}</p>}
                  {!matchListLoading && matchList.length === 0 && !matchListError && (
                    <p className="provider-search-match-empty">No matching availability. Try a different service area or check back later.</p>
                  )}
                  {matchList.length > 0 && (
                    <div className="provider-search-match-hint-row">
                      <span className="provider-search-label-with-info">
                        <span className="provider-search-match-section-label">Matches</span>
                        <button
                          type="button"
                          className="provider-search-info-icon"
                          onClick={(e) => { e.preventDefault(); setMatchHintOpen((v) => !v); }}
                          aria-expanded={matchHintOpen}
                          aria-controls="provider-search-match-hint-info"
                          aria-label="Toggle match hint"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/></svg>
                        </button>
                      </span>
                      {matchHintOpen && (
                        <p className="provider-search-info-text" id="provider-search-match-hint-info">
                          Use <strong>Find matches</strong> to load candidates from central availability. Click a row to see
                          contact details. When everyone agrees, use the check icon under <strong>Select Actual</strong> to
                          choose the provider for this need (you will confirm delivery method next).
                        </p>
                      )}
                    </div>
                  )}
                  {matchList.length > 0 && (
                    <div className="provider-search-match-table-wrap">
                      <table className="provider-search-match-table">
                        <colgroup>
                          <col style={{ width: '34%' }} />
                          <col style={{ width: '12%' }} />
                          <col style={{ width: '12%' }} />
                          <col style={{ width: '30%' }} />
                          <col style={{ width: '12%' }} />
                        </colgroup>
                        <thead>
                          <tr>
                            <th>Provider / Payee</th>
                            <th>IP date</th>
                            <th>LVV date</th>
                            <th>Comment</th>
                            <th className="provider-search-match-action-col" scope="col">
                              <span className="provider-search-match-action-col-label">Select Actual</span>
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {matchList.map((row) => {
                            const providerLabel = [row.provider_last_name, row.provider_first_name].filter(Boolean).join(', ') || '—';
                            const payeePart = row.payee_name ? ` / ${row.payee_name}` : '';
                            const comment = (row.provider_comment || '').trim();
                            const isCommentExpanded = expandedCommentRowId === row.id;
                            const hasLongComment = comment.length > 80;
                            const contact = formatMatchRowContactSummary(row);
                            const contactOpen = matchContactOpenRowId === row.id;
                            return (
                              <Fragment key={row.id}>
                                <tr
                                  className={`provider-search-match-data-row${contactOpen ? ' provider-search-match-data-row-open' : ''}`}
                                  onClick={() => setMatchContactOpenRowId((id) => (id === row.id ? null : row.id))}
                                >
                                  <td>{providerLabel}{payeePart}</td>
                                  <td>{formatDisplayDate(row.ip_date)}</td>
                                  <td>{formatDisplayDate(row.lvv_date)}</td>
                                  <td
                                    className="provider-search-match-comment-cell"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    {comment ? (
                                      <>
                                        <div className={isCommentExpanded ? 'provider-search-match-comment-full' : 'provider-search-match-comment-clipped'}>
                                          {comment}
                                        </div>
                                        {hasLongComment && (
                                          <button
                                            type="button"
                                            className="provider-search-match-comment-toggle"
                                            onClick={() => setExpandedCommentRowId(isCommentExpanded ? null : row.id)}
                                            aria-expanded={isCommentExpanded}
                                          >
                                            {isCommentExpanded ? 'Collapse' : 'View'}
                                          </button>
                                        )}
                                      </>
                                    ) : (
                                      '—'
                                    )}
                                  </td>
                                  <td className="provider-search-match-action-cell" onClick={(e) => e.stopPropagation()}>
                                    <button
                                      type="button"
                                      className="provider-search-select-actual-btn"
                                      onClick={() => {
                                        setMatchContactOpenRowId(null);
                                        setMatchDialogRow(row);
                                        setMatchDialogMode('');
                                        // Delivery start date — the date service actually begins (may be in the future).
                                        // Default to any existing delivery start on the need, else today (editable).
                                        const existingStart = (editForm.service_delivery_start_date || '').trim();
                                        const d = new Date();
                                        const todayLocal = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
                                        setMatchDialogDeliveryStart(existingStart || todayLocal);
                                        setCommitMatchError(null);
                                      }}
                                      disabled={committingMatch}
                                      title="Select this provider as the actual provider for this need"
                                      aria-label={`Select ${providerLabel} as actual provider for this need`}
                                    >
                                      {committingMatch ? (
                                        <span className="provider-search-select-actual-busy" aria-hidden="true">…</span>
                                      ) : (
                                        <svg className="provider-search-select-actual-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                          <path
                                            d="M6.5 12.5 10 16 17.5 8.5"
                                            stroke="currentColor"
                                            strokeWidth="2.25"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                          />
                                        </svg>
                                      )}
                                    </button>
                                  </td>
                                </tr>
                                {contactOpen && (
                                  <tr className="provider-search-match-contact-panel-row">
                                    <td colSpan={5}>
                                      <div className="provider-search-match-contact-panel" role="region" aria-label="Provider contact">
                                        <div className="provider-search-match-contact-panel-title">Contact</div>
                                        <dl className="provider-search-match-contact-dl">
                                          {contact.email ? (
                                            <>
                                              <dt>Email</dt>
                                              <dd>
                                                <a href={`mailto:${encodeURIComponent(contact.email)}`}>{contact.email}</a>
                                              </dd>
                                            </>
                                          ) : (
                                            <>
                                              <dt>Email</dt>
                                              <dd>—</dd>
                                            </>
                                          )}
                                          {contact.phone ? (
                                            <>
                                              <dt>Phone</dt>
                                              <dd>
                                                <a href={`tel:${contact.phone.replace(/\s/g, '')}`}>{contact.phone}</a>
                                              </dd>
                                            </>
                                          ) : (
                                            <>
                                              <dt>Phone</dt>
                                              <dd>—</dd>
                                            </>
                                          )}
                                          <dt>Primary office</dt>
                                          <dd>
                                            {contact.hasAddress ? (
                                              <>
                                                {contact.street ? <div>{contact.street}</div> : null}
                                                {contact.cityLine ? <div>{contact.cityLine}</div> : null}
                                                {!contact.street && !contact.cityLine ? <span>—</span> : null}
                                              </>
                                            ) : (
                                              <span>—</span>
                                            )}
                                            {(contact.sitePhone || contact.siteEmail) && (
                                              <div className="provider-search-match-contact-site-extras">
                                                {contact.sitePhone ? <div>Office phone: {contact.sitePhone}</div> : null}
                                                {contact.siteEmail ? (
                                                  <div>
                                                    Office email:{' '}
                                                    <a href={`mailto:${encodeURIComponent(contact.siteEmail)}`}>{contact.siteEmail}</a>
                                                  </div>
                                                ) : null}
                                              </div>
                                            )}
                                          </dd>
                                        </dl>
                                        <p className="provider-search-match-contact-panel-footnote">
                                          Address is the first office location on file (by sort order).
                                        </p>
                                      </div>
                                    </td>
                                  </tr>
                                )}
                              </Fragment>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                  <div className="list-edit-form-actions provider-search-match-actions">
                    <button
                      type="button"
                      className="btn-primary"
                      onClick={() => {
                        setShowMatchList(false);
                        setMatchListError(null);
                        setCommitMatchError(null);
                        setCommitMatchSuccess(null);
                        setExpandedCommentRowId(null);
                        setMatchContactOpenRowId(null);
                      }}
                    >
                      Close
                    </button>
                  </div>
                </div>
              )}
              {matchDialogRow && (
                <div className="provider-search-match-dialog-overlay" role="dialog" aria-modal="true" aria-labelledby="match-dialog-title">
                  <div className="provider-search-match-dialog">
                    <h3 id="match-dialog-title" className="provider-search-match-dialog-title">Match this provider</h3>
                    <p className="provider-search-match-dialog-desc">This sets the actual provider and adds them to the Provider Team. The delivery start date and delivery method are recorded on the need, the need is closed as of today, and the frequency and delivery start date are copied to the team.</p>
                    <div className="provider-search-match-dialog-form">
                      <div className="form-group">
                        <label htmlFor="match-dialog-close">Delivery start date *</label>
                        <input
                          id="match-dialog-close"
                          type="date"
                          value={matchDialogDeliveryStart}
                          onChange={(e) => setMatchDialogDeliveryStart(e.target.value)}
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="match-dialog-mode">Delivery method *</label>
                        <select
                          id="match-dialog-mode"
                          value={matchDialogMode}
                          onChange={(e) => setMatchDialogMode(e.target.value)}
                          required
                        >
                          <option value="">Select…</option>
                          <option value="IN_PERSON">In Person</option>
                          <option value="LVV">Live Video Visit (LVV)</option>
                          <option value="BOTH">Both: In Person / LVV</option>
                        </select>
                      </div>
                    </div>
                    {commitMatchError && <p className="provider-search-form-error">{commitMatchError}</p>}
                    <div className="provider-search-match-dialog-actions">
                      <button type="button" className="btn-cancel" onClick={() => { setMatchDialogRow(null); setCommitMatchError(null); }}>
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="btn-primary"
                        disabled={committingMatch || !matchDialogMode || !matchDialogDeliveryStart}
                        onClick={() => handleCommitMatch(matchDialogRow, { service_delivery_mode: matchDialogMode, service_delivery_start_date: matchDialogDeliveryStart })}
                      >
                        {committingMatch ? 'Saving…' : 'Confirm selection'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
              {editFormLoading ? (
                <p>Loading…</p>
              ) : (
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    void handleEditNeedSave();
                  }}
                >
                  <p className="provider-search-edit-need-save-hint">Click <strong>Save</strong> at the top or bottom when you are done. Changes are not saved automatically.</p>
                  <div className="provider-search-edit-form-client-row">
                    <span><strong>Assigned SC:</strong> {editFormAssignedScName || '—'}</span>
                  </div>
                  <div className="provider-search-form-grid">
                    <label>
                      Service type *
                      <select
                        value={editForm.need_type_id}
                        onChange={(e) => {
                          const v = e.target.value;
                          setEditNeedDirty(true);
                          setEditForm((f) => {
                            const next = { ...f, need_type_id: v };
                            if (!needTypeIdIsDgn(needFormProviderTypes, v)) {
                              next.diagnostic_reason = '';
                              next.autism_family_discussed = null;
                            }
                            return next;
                          });
                        }}
                        required
                      >
                        <option value="">Select type…</option>
                        {needFormProviderTypes.filter(isClientServiceNeedType).map((pt) => (
                          <option key={pt.id} value={pt.id}>{pt.code || pt.name}</option>
                        ))}
                      </select>
                    </label>
                    <label>
                      Service area{!editFormNeedIsDgn ? ' *' : ''}
                      <select
                        value={editForm.service_area_id}
                        onChange={(e) => { setEditNeedDirty(true); setEditForm((f) => ({ ...f, service_area_id: e.target.value })); }}
                        required={!editFormNeedIsDgn}
                      >
                        <option value="">{editFormNeedIsDgn ? 'Optional' : 'Select…'}</option>
                        {needFormServiceAreas.map((sa) => (
                          <option key={sa.id} value={sa.id}>{sa.name || sa.code}</option>
                        ))}
                      </select>
                    </label>
                    <label>
                      Date of Consent *
                      <input
                        type="date"
                        value={editForm.need_start}
                        onChange={(e) => { setEditNeedDirty(true); setEditForm((f) => ({ ...f, need_start: e.target.value })); }}
                        required
                      />
                    </label>
                    {editFormNeedIsDgn && (
                      <>
                        <label>
                          Diagnostic reason *
                          <select
                            value={editForm.diagnostic_reason}
                            onChange={(e) => {
                              setEditNeedDirty(true);
                              setEditForm((f) => ({
                                ...f,
                                diagnostic_reason: e.target.value,
                                autism_family_discussed: e.target.value !== 'autism' ? null : f.autism_family_discussed,
                              }));
                            }}
                            required
                          >
                            <option value="">Select…</option>
                            {DGN_DIAGNOSTIC_REASON_OPTIONS.map((o) => (
                              <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                          </select>
                        </label>
                        {editForm.diagnostic_reason === 'autism' && (
                          <div className="provider-search-autism-discussion">
                            <p className="provider-search-autism-discussion-label">Has the possibility of autism been discussed with the family? *</p>
                            <div className="provider-search-autism-discussion-options">
                              <label className="provider-search-autism-discussion-option">
                                <input
                                  type="radio"
                                  name="autism_family_discussed_edit"
                                  checked={editForm.autism_family_discussed === true}
                                  onChange={() => { setEditNeedDirty(true); setEditForm((f) => ({ ...f, autism_family_discussed: true })); }}
                                />
                                Yes
                              </label>
                              <label className="provider-search-autism-discussion-option">
                                <input
                                  type="radio"
                                  name="autism_family_discussed_edit"
                                  checked={editForm.autism_family_discussed === false}
                                  onChange={() => { setEditNeedDirty(true); setEditForm((f) => ({ ...f, autism_family_discussed: false })); }}
                                />
                                No
                              </label>
                            </div>
                            {editForm.autism_family_discussed === false && (
                              <p className="provider-search-autism-discussion-advisory">
                                Please add context in the SC Note field about why this has not yet been discussed.
                              </p>
                            )}
                          </div>
                        )}
                      </>
                    )}
                    {!editFormNeedIsDgn && (
                    <label>
                      Frequency *
                      <select
                        value={editForm.frequency_id}
                        onChange={(e) => { setEditNeedDirty(true); setEditForm((f) => ({ ...f, frequency_id: e.target.value })); }}
                        required
                      >
                        <option value="">Select frequency…</option>
                        {frequencies.map((f) => (
                          <option key={f.id} value={f.id}>{f.description || f.code}</option>
                        ))}
                      </select>
                    </label>
                    )}
                    <label>
                      Status
                      <select
                        value={editForm.status}
                        onChange={(e) => { setEditNeedDirty(true); setEditForm((f) => ({ ...f, status: e.target.value })); }}
                      >
                        <option value="open">Open</option>
                        <option value="matched">Matched</option>
                        <option value="closed">Closed</option>
                      </select>
                    </label>
                    <label className="provider-search-need-close-date-label">
                      <span className="provider-search-label-with-info">
                        Need close date
                        <InfoIconButton
                          className="provider-search-close-date-info"
                          onClick={(e) => {
                            e.preventDefault();
                            setNeedCloseDateHintEditOpen((v) => !v);
                          }}
                          ariaExpanded={needCloseDateHintEditOpen}
                          ariaControls="provider-search-need-close-date-info-edit"
                          ariaLabel="Show close date and status explanation"
                          ariaLabelExpanded="Hide close date and status explanation"
                        />
                      </span>
                      <input
                        type="date"
                        value={editForm.need_close}
                        onChange={(e) => { setEditNeedDirty(true); setEditForm((f) => ({ ...f, need_close: e.target.value })); }}
                      />
                      {needCloseDateHintEditOpen && (
                        <p className="provider-search-info-text" id="provider-search-need-close-date-info-edit">
                          The search is over! This is the date you matched a provider for this need. (Saving with a close date sets status to Closed automatically if it was Open.)
                        </p>
                      )}
                    </label>
                    {!editFormNeedIsDgn && (
                    <label className="provider-search-form-check">
                      <input
                        type="checkbox"
                        checked={editForm.need_feeding === true}
                        onChange={(e) => { setEditNeedDirty(true); setEditForm((f) => ({ ...f, need_feeding: e.target.checked })); }}
                      />
                      <span>Feeding</span>
                    </label>
                    )}
                  </div>
                  {!editFormNeedIsDgn && (
                  <label className="provider-search-form-note provider-search-family-availability-label">
                    <span className="provider-search-label-with-info">
                      Family availability
                      <button
                        type="button"
                        className="provider-search-info-icon"
                        onClick={(e) => { e.preventDefault(); setFamilyAvailabilityHintOpen((v) => !v); }}
                        aria-expanded={familyAvailabilityHintOpen}
                        aria-controls="provider-search-family-availability-info-edit"
                        aria-label="Toggle family availability info"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/></svg>
                      </button>
                    </span>
                    {familyAvailabilityHintOpen && (
                      <p className="provider-search-info-text" id="provider-search-family-availability-info-edit">
                        Do NOT include PII. Do NOT include Method. (Providers see this field.)
                      </p>
                    )}
                    <input
                      type="text"
                      className="provider-search-family-availability-input"
                      value={editForm.family_availability}
                      onChange={(e) => { setEditNeedDirty(true); setEditForm((f) => ({ ...f, family_availability: e.target.value })); }}
                      placeholder="Optional"
                    />
                  </label>
                  )}
                  <label className="provider-search-form-note">
                    <span className="provider-search-label-with-info">
                      SC note
                      <InfoIconButton
                        onClick={(e) => { e.preventDefault(); setScNoteHintOpen((v) => !v); }}
                        ariaExpanded={scNoteHintOpen}
                        ariaControls="provider-search-sc-note-info-edit"
                        ariaLabel="Show SC note info"
                        ariaLabelExpanded="Hide SC note info"
                      />
                    </span>
                    {scNoteHintOpen && (
                      <p className="provider-search-info-text" id="provider-search-sc-note-info-edit">
                        Family preferences for method and other notes about providers not to use, etc. (This is just for you.)
                      </p>
                    )}
                    <textarea
                      value={editForm.sc_note}
                      onChange={(e) => { setEditNeedDirty(true); setEditForm((f) => ({ ...f, sc_note: e.target.value })); }}
                      rows={2}
                      placeholder="Optional coordination notes"
                    />
                  </label>
                  {!editFormNeedIsDgn && (
                    <div className="provider-search-need-subsection">
                      <div className="client-form-section-collapse-bar">
                        <span className="client-form-section-collapse-label">Delay information</span>
                        <button
                          type="button"
                          className="section-collapse-toggle"
                          onClick={() => setDelayInfoOpen((v) => !v)}
                          aria-expanded={delayInfoOpen}
                        >
                          {delayInfoOpen ? 'Hide' : 'Show'}
                        </button>
                      </div>
                      {canReclassifyNeed && editingNeedId && editForm.delay_start_date && !editForm.delay_end_date && (
                        <div className="provider-search-reclassify">
                          <span className="provider-search-reclassify-label">Classification</span>
                          <div className="provider-search-reclassify-toggle" role="group" aria-label="Delay or Change">
                            <button
                              type="button"
                              className={`provider-search-reclassify-btn${editForm.issue_type === 'change' ? '' : ' is-active'}`}
                              onClick={() => handleReclassify('delay')}
                              disabled={editFormSaving}
                              aria-pressed={editForm.issue_type !== 'change'}
                            >
                              Delay
                            </button>
                            <button
                              type="button"
                              className={`provider-search-reclassify-btn${editForm.issue_type === 'change' ? ' is-active is-change' : ''}`}
                              onClick={() => handleReclassify('change')}
                              disabled={editFormSaving}
                              aria-pressed={editForm.issue_type === 'change'}
                            >
                              Change
                            </button>
                          </div>
                          <p className="provider-search-reclassify-help">
                            A <strong>change</strong> means the child already has this service — it is not a delay and is never reported to the state. Click <strong>Save</strong> to apply.
                          </p>
                        </div>
                      )}
                      {delayInfoOpen && (
                      <>
                      <div className="provider-search-form-grid">
                        <label>
                          Delay start
                          <input
                            type="date"
                            value={editForm.delay_start_date}
                            onChange={(e) => { setEditNeedDirty(true); setEditForm((f) => ({ ...f, delay_start_date: e.target.value })); }}
                          />
                        </label>
                        <label>
                          Delay reason{editForm.delay_start_date && editForm.issue_type !== 'change' ? ' *' : ''}
                          <select
                            value={editForm.delay_reason_id}
                            onChange={(e) => { setEditNeedDirty(true); setEditForm((f) => ({ ...f, delay_reason_id: e.target.value })); }}
                            required={!!editForm.delay_start_date && editForm.issue_type !== 'change'}
                          >
                            <option value="">Select reason…</option>
                            {delayReasons.map((dr) => (
                              <option key={String(dr.id)} value={String(dr.id)}>{dr.code} - {dr.description}</option>
                            ))}
                          </select>
                        </label>
                        <label>
                          Delay reported to DHS
                          <input
                            type="date"
                            value={editForm.delay_reported_to_dhs_date}
                            onChange={(e) => { setEditNeedDirty(true); setEditForm((f) => ({ ...f, delay_reported_to_dhs_date: e.target.value })); }}
                          />
                        </label>
                      </div>
                      <label className="provider-search-form-note">
                        Delay comment
                        <textarea
                          value={editForm.delay_comment}
                          rows={2}
                          onChange={(e) => { setEditNeedDirty(true); setEditForm((f) => ({ ...f, delay_comment: e.target.value })); }}
                          placeholder="Optional"
                        />
                      </label>
                      </>
                      )}
                    </div>
                  )}
                  <div
                    className="provider-search-actual-provider-label"
                    role="group"
                    aria-labelledby="edit-need-actual-provider-heading"
                  >
                    <span id="edit-need-actual-provider-heading" className="provider-search-actual-provider-field-heading">
                      Actual provider
                    </span>
                    <SearchableProviderTeamOfficeSelect
                      id="edit-need-actual-provider-office"
                      value={editForm.actual_provider_office_value}
                      displayLabel={editForm.actualProviderOfficeDisplayLabel}
                      onChange={({ value, label, providerId, cleared }) => {
                        setEditNeedDirty(true);
                        if (cleared === true) {
                          needActualProviderExplicitClearRef.current = true;
                        } else if (value != null && String(value).trim() !== '') {
                          needActualProviderExplicitClearRef.current = false;
                        }
                        flushSync(() => {
                          setEditForm((f) => ({
                            ...f,
                            actual_provider_office_value: value || '',
                            actualProviderOfficeDisplayLabel: label || '',
                            actual_provider_id: providerId || '',
                          }));
                        });
                      }}
                      typeCode={editNeedTypeCode || undefined}
                      placeholder="Click and type to search provider and office…"
                      disabled={!canManageNeeds}
                      className="provider-search-actual-provider-select"
                    />
                  </div>
                  {!editFormNeedIsDgn && (
                    <div className="provider-search-need-subsection">
                      <div className="provider-search-form-grid">
                        <label>
                          Delivery method
                          <select
                            value={editForm.service_delivery_mode}
                            onChange={(e) => { setEditNeedDirty(true); setEditForm((f) => ({ ...f, service_delivery_mode: e.target.value })); }}
                          >
                            <option value="">Select method…</option>
                            <option value="IN_PERSON">In Person</option>
                            <option value="LVV">Live Video Visit (LVV)</option>
                            <option value="BOTH">Both: In person / LVV</option>
                          </select>
                        </label>
                        <label>
                          <span className="provider-search-label-with-info">
                            Delivery start
                            <InfoIconButton
                              onClick={(e) => { e.preventDefault(); setDeliveryStartHintEditOpen((v) => !v); }}
                              ariaExpanded={deliveryStartHintEditOpen}
                              ariaControls="provider-search-delivery-start-info-edit"
                              ariaLabel="Show delivery start info"
                              ariaLabelExpanded="Hide delivery start info"
                            />
                          </span>
                          <input
                            type="date"
                            value={editForm.service_delivery_start_date}
                            onChange={(e) => {
                              const v = e.target.value;
                              setEditNeedDirty(true);
                              setEditForm((f) => ({
                                ...f,
                                service_delivery_start_date: v,
                                // Delay End defaults to Delivery Start (editable) when a delay is open and no end set.
                                delay_end_date: (f.delay_start_date && !f.delay_end_date) ? v : f.delay_end_date,
                              }));
                            }}
                          />
                          {deliveryStartHintEditOpen && (
                            <p className="provider-search-info-text" id="provider-search-delivery-start-info-edit">
                              This is the auth start date — the date services actually begin.
                            </p>
                          )}
                        </label>
                        <label>
                          Delay end
                          <input
                            type="date"
                            value={editForm.delay_end_date}
                            onChange={(e) => { setEditNeedDirty(true); setEditForm((f) => ({ ...f, delay_end_date: e.target.value })); }}
                          />
                        </label>
                      </div>
                    </div>
                  )}
                  <div className="provider-search-edit-form-actions list-edit-form-actions">
                    <button type="button" className="btn-cancel" onClick={handleEditNeedCancel} disabled={editFormSaving}>
                      Cancel
                    </button>
                    <button type="button" className="btn-save" disabled={!editNeedDirty || editFormSaving} onClick={handleEditNeedSave}>
                      {editFormSaving ? 'Saving…' : 'Save'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          )}
          {needsLoading ? (
            <p>Loading needs…</p>
          ) : (
            <>
              <div className="provider-search-needs-summary-row">
                <div className="provider-search-needs-summary">
                  {(() => {
                    const onClient = !!effectiveClientId;
                    if (needsTotal === 0) {
                      if (needsStatusFilter === 'open') return onClient ? 'No open needs for this client.' : 'No open needs.';
                      if (needsStatusFilter === 'matched') return onClient ? 'No matched needs for this client.' : 'No matched needs.';
                      if (needsStatusFilter === 'closed') return onClient ? 'No closed needs for this client.' : 'No closed needs.';
                      return onClient ? 'No needs for this client.' : 'No needs found.';
                    }
                    if (needsStatusFilter === 'open') return needsTotal === 1 ? '1 open need' : `${needsTotal} open needs`;
                    if (needsStatusFilter === 'matched') return needsTotal === 1 ? '1 matched need' : `${needsTotal} matched needs`;
                    if (needsStatusFilter === 'closed') return needsTotal === 1 ? '1 closed need' : `${needsTotal} closed needs`;
                    return needsTotal === 1 ? '1 need' : `${needsTotal} needs`;
                  })()}
                </div>
                <button
                  type="button"
                  className={`needs-view-toggle-btn${needsView === 'availability' ? ' needs-view-toggle-btn--active' : ''}`}
                  onClick={() => setNeedsView((v) => v === 'availability' ? 'standard' : 'availability')}
                  title={needsView === 'availability' ? 'Switch to standard view' : 'Switch to Family Availability view'}
                >
                  {needsView === 'availability' ? 'Standard view' : 'Family Availability view'}
                </button>
              </div>
              {needsView === 'availability' ? (
                <table className={`provider-search-needs-table needs-table-availability${effectiveClientId ? ' needs-table-client-context' : ''}`}>
                  <thead>
                    <tr>
                      {!effectiveClientId && <th className="col-client">Client</th>}
                      <th
                        className="col-type needs-avail-th-sortable"
                        onClick={() => setAvailSort((s) => ({ by: 'type', dir: s.by === 'type' ? (s.dir === 'asc' ? 'desc' : 'asc') : 'asc' }))}
                        aria-sort={availSort.by === 'type' ? (availSort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
                        title="Sort by type"
                      >
                        Type{availSort.by === 'type' ? (availSort.dir === 'asc' ? ' ↑' : ' ↓') : ''}
                      </th>
                      <th
                        className="col-avail needs-avail-th-sortable"
                        onClick={() => setAvailSort((s) => ({ by: 'avail', dir: s.by === 'avail' ? (s.dir === 'asc' ? 'desc' : 'asc') : 'desc' }))}
                        aria-sort={availSort.by === 'avail' ? (availSort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
                        title="Sort by family availability (blanks always last)"
                      >
                        Family Availability{availSort.by === 'avail' ? (availSort.dir === 'desc' ? ' ↓' : ' ↑') : ''}
                      </th>
                      <th className="col-feeding">Feeding</th>
                    </tr>
                  </thead>
                  <tbody>
                    {availSortedNeeds.length === 0 ? (
                      <tr>
                        <td colSpan={effectiveClientId ? 3 : 4}>No needs found.</td>
                      </tr>
                    ) : (
                      availSortedNeeds.map((n) => (
                        <tr
                          key={n.id}
                          className={canViewNeeds ? 'clickable-row' : ''}
                          onClick={canViewNeeds ? () => openEditForm(n) : undefined}
                        >
                          {!effectiveClientId && (
                            <td className="col-client">
                              {[n.client_last_name, n.client_first_name].filter(Boolean).join(', ') || n.client_id}
                              {n.delay_start_date && (n.issue_type === 'change'
                                ? <span className="provider-search-delay-badge provider-search-change-badge">Change</span>
                                : <span className="provider-search-delay-badge">Delay</span>)}
                            </td>
                          )}
                          <td className="col-type">{n.need_type_code || n.need_type_name || n.need_type_id}
                            {effectiveClientId && n.delay_start_date && (n.issue_type === 'change'
                              ? <span className="provider-search-delay-badge provider-search-change-badge">Change</span>
                              : <span className="provider-search-delay-badge">Delay</span>)}</td>
                          <td className="col-avail">{n.family_availability || <span className="needs-avail-empty">—</span>}</td>
                          <td className="col-feeding">{n.need_feeding ? 'Yes' : 'No'}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              ) : (
                <table className={`provider-search-needs-table${effectiveClientId ? ' needs-table-client-context' : ''}`}>
                  <thead>
                    <tr>
                      {!effectiveClientId && <th className="col-client">Client</th>}
                      <th className="col-type">Type</th>
                      <th className="col-service-area">Service area</th>
                      <th className="col-frequency">Frequency</th>
                      <th className="col-status">Status</th>
                      <th className="col-start-date">Start date</th>
                    </tr>
                    <tr className="provider-search-needs-filter-row">
                      {!effectiveClientId && (
                        <th className="col-client">
                          <input
                            type="text"
                            className="provider-search-needs-filter-input"
                            placeholder="Search by client..."
                            value={needsClientSearch}
                            onChange={(e) => setNeedsClientSearch(e.target.value)}
                            aria-label="Filter by client name"
                          />
                        </th>
                      )}
                      <th className="col-type">
                        <LightFilterSelect
                          portalList
                          className="provider-search-needs-filter-select"
                          options={[
                            { value: '', label: 'All' },
                            ...needFormProviderTypes.filter(isClientServiceNeedType).map((pt) => ({
                              value: String(pt.id),
                              label: pt.code || pt.name || String(pt.id),
                            })),
                          ]}
                          value={String(needsTypeFilter ?? '')}
                          onChange={(v) => setNeedsTypeFilter(v)}
                          aria-label="Filter by type"
                        />
                      </th>
                      <th className="col-service-area">
                        <LightFilterSelect
                          portalList
                          className="provider-search-needs-filter-select"
                          options={[
                            { value: '', label: 'All' },
                            ...needFormServiceAreas.map((sa) => ({
                              value: String(sa.id),
                              label: sa.name || sa.code || String(sa.id),
                            })),
                          ]}
                          value={String(needsServiceAreaFilter ?? '')}
                          onChange={(v) => setNeedsServiceAreaFilter(v)}
                          aria-label="Filter by service area"
                        />
                      </th>
                      <th className="col-frequency">
                        <LightFilterSelect
                          portalList
                          className="provider-search-needs-filter-select light-filter-select--frequency"
                          options={[
                            { value: '', label: 'All' },
                            ...frequencies.map((f) => ({
                              value: String(f.id),
                              label: f.description || f.code || String(f.id),
                            })),
                          ]}
                          value={String(needsFrequencyFilter ?? '')}
                          onChange={(v) => setNeedsFrequencyFilter(v)}
                          aria-label="Filter by frequency"
                        />
                      </th>
                      <th className="col-status">
                        <LightFilterSelect
                          portalList
                          className="provider-search-needs-filter-select light-filter-select--narrow"
                          options={[
                            { value: 'all', label: 'All' },
                            { value: 'open', label: 'Open' },
                            { value: 'matched', label: 'Matched' },
                            { value: 'closed', label: 'Closed' },
                          ]}
                          value={needsStatusFilter || 'open'}
                          onChange={(v) => setNeedsStatusFilter(v)}
                          aria-label="Filter by status"
                        />
                      </th>
                      <th className="col-start-date" />
                    </tr>
                  </thead>
                  <tbody>
                    {needs.length === 0 ? (
                      <tr>
                        <td colSpan={effectiveClientId ? 5 : 6}>
                          {(() => {
                            const onClient = !!effectiveClientId;
                            if (needsStatusFilter === 'open') return onClient ? 'No open needs for this client.' : 'No open needs.';
                            if (needsStatusFilter === 'matched') return onClient ? 'No matched needs for this client.' : 'No matched needs.';
                            if (needsStatusFilter === 'closed') return onClient ? 'No closed needs for this client.' : 'No closed needs.';
                            return onClient ? 'No needs for this client.' : 'No needs found.';
                          })()}
                        </td>
                      </tr>
                    ) : (
                      needs.map((n) => (
                        <tr
                          key={n.id}
                          className={canViewNeeds ? 'clickable-row' : ''}
                          onClick={canViewNeeds ? () => openEditForm(n) : undefined}
                        >
                          {!effectiveClientId && (
                            <td className="col-client">
                              {[n.client_last_name, n.client_first_name].filter(Boolean).join(', ') || n.client_id}
                              {n.delay_start_date && (n.issue_type === 'change'
                                ? <span className="provider-search-delay-badge provider-search-change-badge">Change</span>
                                : <span className="provider-search-delay-badge">Delay</span>)}
                            </td>
                          )}
                          <td className="col-type">{n.need_type_code || n.need_type_name || n.need_type_id}
                            {effectiveClientId && n.delay_start_date && (n.issue_type === 'change'
                              ? <span className="provider-search-delay-badge provider-search-change-badge">Change</span>
                              : <span className="provider-search-delay-badge">Delay</span>)}</td>
                          <td className="col-service-area">{n.service_area_name || n.service_area_code || '—'}</td>
                          <td className="col-frequency">{n.frequency_description || n.frequency_code || '—'}</td>
                          <td className="col-status">{n.status}</td>
                          <td className="col-start-date">{formatDisplayDate(n.need_start)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              )}
              {needsHasMore && <div ref={needsSentinelRef} className="infinite-scroll-sentinel" aria-hidden="true" />}
              {needsLoadingMore && <div className="infinite-scroll-loading">Loading more...</div>}
            </>
          )}
        </section>
            </>
          )}

          {activeSection === 'search' && (
        <section className="provider-search-search">
          <div className="provider-search-mode-tabs" role="tablist" aria-label="Provider search mode">
            <div className="provider-search-mode-tab-wrap">
              <button
                type="button"
                role="tab"
                aria-selected={providerSearchMode === 'directory'}
                className={`provider-search-mode-tab ${providerSearchMode === 'directory' ? 'active' : ''}`}
                onClick={() => { setProviderSearchMode('directory'); setProvenError(null); }}
              >
                Directory Search
              </button>
              <InfoIconButton
                className="provider-search-mode-tab-info"
                onClick={(e) => { e.preventDefault(); setDirectorySearchInfoOpen((v) => !v); }}
                ariaExpanded={directorySearchInfoOpen && providerSearchMode === 'directory'}
                ariaControls="provider-search-directory-mode-info"
                ariaLabel="Show Directory Search explanation"
                ariaLabelExpanded="Hide Directory Search explanation"
              />
            </div>
            <div className="provider-search-mode-tab-wrap">
              <button
                type="button"
                role="tab"
                aria-selected={providerSearchMode === 'proven'}
                className={`provider-search-mode-tab ${providerSearchMode === 'proven' ? 'active' : ''}`}
                onClick={() => { setProviderSearchMode('proven'); setSearchError(null); }}
              >
                Proven From Needs
              </button>
              <InfoIconButton
                className="provider-search-mode-tab-info"
                onClick={(e) => { e.preventDefault(); setProvenNeedsInfoOpen((v) => !v); }}
                ariaExpanded={provenNeedsInfoOpen && providerSearchMode === 'proven'}
                ariaControls="provider-search-proven-mode-info"
                ariaLabel="Show Proven From Needs explanation"
                ariaLabelExpanded="Hide Proven From Needs explanation"
              />
            </div>
          </div>
          {directorySearchInfoOpen && providerSearchMode === 'directory' && (
            <p className="provider-search-mode-hint provider-search-info-text" id="provider-search-directory-mode-info">
              Search the provider directory by type, service area, credentials, and languages. Click a filter to open it, choose options, then Apply. Leave a filter cleared (All) to include every value for that dimension. Then run Search. Results are not tied to a specific client need.
            </p>
          )}
          {provenNeedsInfoOpen && providerSearchMode === 'proven' && (
            <p className="provider-search-mode-hint provider-search-info-text" id="provider-search-proven-mode-info">
              Lists providers who were set as <strong>Actual provider</strong> on <strong>matched or closed</strong> service needs, where the <strong>need’s</strong> service type and service area match your selections. Only children who are <strong>four years old or younger</strong> by date of birth (they have not reached their fifth birthday) are included. Service area comes from the <strong>need</strong> record, not the provider directory. Needs with a start date older than four years are excluded. Each provider appears once in the results.
            </p>
          )}
          {providerSearchMode === 'directory' && (
            <>
          <div className="provider-search-directory-filters">
            {renderDirectoryFilterPopoverPortal()}
            <div className="provider-search-directory-filter-bar" role="toolbar" aria-label="Directory search filters">
              <div className="provider-search-dir-trigger-wrap">
                <button
                  type="button"
                  className={`provider-search-dir-trigger${filters.providerTypeIds.length ? ' is-active' : ''}`}
                  aria-expanded={dirFilterPopoverKey === 'providerTypes'}
                  aria-haspopup="dialog"
                  onClick={(e) => handleDirectoryFilterTrigger('providerTypes', e)}
                >
                  <span className="provider-search-dir-trigger-funnel" aria-hidden="true">
                    <DirectoryFilterFunnelIcon />
                  </span>
                  <span className="provider-search-dir-trigger-text">
                    <span className="provider-search-dir-trigger-label">Provider types</span>
                    <span className="provider-search-dir-trigger-summary">
                      {directoryFilterTriggerSummary(filters.providerTypeIds, directoryProviderTypeOptions)}
                    </span>
                  </span>
                </button>
              </div>
              <div className="provider-search-dir-trigger-wrap">
                <button
                  type="button"
                  className={`provider-search-dir-trigger${filters.serviceAreaIds.length ? ' is-active' : ''}`}
                  aria-expanded={dirFilterPopoverKey === 'serviceAreas'}
                  aria-haspopup="dialog"
                  onClick={(e) => handleDirectoryFilterTrigger('serviceAreas', e)}
                >
                  <span className="provider-search-dir-trigger-funnel" aria-hidden="true">
                    <DirectoryFilterFunnelIcon />
                  </span>
                  <span className="provider-search-dir-trigger-text">
                    <span className="provider-search-dir-trigger-label">Service areas</span>
                    <span className="provider-search-dir-trigger-summary">
                      {directoryFilterTriggerSummary(filters.serviceAreaIds, directoryServiceAreaOptions)}
                    </span>
                  </span>
                </button>
              </div>
              <div className="provider-search-dir-trigger-wrap">
                <button
                  type="button"
                  className={`provider-search-dir-trigger${filters.credentialIds.length ? ' is-active' : ''}`}
                  aria-expanded={dirFilterPopoverKey === 'credentials'}
                  aria-haspopup="dialog"
                  onClick={(e) => handleDirectoryFilterTrigger('credentials', e)}
                >
                  <span className="provider-search-dir-trigger-funnel" aria-hidden="true">
                    <DirectoryFilterFunnelIcon />
                  </span>
                  <span className="provider-search-dir-trigger-text">
                    <span className="provider-search-dir-trigger-label">Credentials</span>
                    <span className="provider-search-dir-trigger-summary">
                      {directoryFilterTriggerSummary(filters.credentialIds, directoryCredentialOptions)}
                    </span>
                  </span>
                </button>
              </div>
              <div className="provider-search-dir-trigger-wrap">
                <button
                  type="button"
                  className={`provider-search-dir-trigger${filters.languageIds.length ? ' is-active' : ''}`}
                  aria-expanded={dirFilterPopoverKey === 'languages'}
                  aria-haspopup="dialog"
                  onClick={(e) => handleDirectoryFilterTrigger('languages', e)}
                >
                  <span className="provider-search-dir-trigger-funnel" aria-hidden="true">
                    <DirectoryFilterFunnelIcon />
                  </span>
                  <span className="provider-search-dir-trigger-text">
                    <span className="provider-search-dir-trigger-label">Languages</span>
                    <span className="provider-search-dir-trigger-summary">
                      {directoryFilterTriggerSummary(filters.languageIds, directoryLanguageOptions)}
                    </span>
                  </span>
                </button>
              </div>
            </div>
            <div className="provider-search-directory-filter-footer">
              <label className="provider-search-check">
                <input type="checkbox" checked={filters.nonAssistant} onChange={(e) => setFilters((f) => ({ ...f, nonAssistant: e.target.checked }))} />
                Non-assistant only
              </label>
              <button type="button" className="btn-primary" onClick={handleSearchProviders} disabled={searching}>
                {searching ? 'Searching…' : 'Search'}
              </button>
            </div>
          </div>
          {searchError && (
            <div className="provider-search-error" role="alert">
              {searchError}
            </div>
          )}
          {hasSearched && (
            <div className="provider-search-results-summary">
              {resultsTotal === 0
                ? 'No providers match your filters.'
                : resultsTotal === 1
                  ? '1 provider found'
                  : `${resultsTotal} providers found`}
            </div>
          )}
          <div className="provider-search-results-actions" ref={actionsDropdownRef}>
            {searchResults.length > 0 && (
              <>
                <button
                  type="button"
                  className="actions-dropdown-toggle"
                  onClick={() => setActionsDropdownOpen((v) => !v)}
                  aria-expanded={actionsDropdownOpen}
                  aria-haspopup="true"
                >
                  Use results ▼
                </button>
                {actionsDropdownOpen && (
                  <div className="actions-dropdown-menu" role="menu">
                    <button type="button" role="menuitem" onClick={handleCopyBcc}>Copy BCC list</button>
                    <button type="button" role="menuitem" onClick={handleMailto}>Open email (BCC)</button>
                    <button type="button" role="menuitem" onClick={handleExportCsv}>Export to CSV</button>
                  </div>
                )}
              </>
            )}
          </div>
          <table className="provider-search-results-table">
            <thead>
              <tr>
                <th>Provider</th>
                <th>Type</th>
                <th>Provider email</th>
                <th>Payee</th>
                <th>Payee email</th>
              </tr>
            </thead>
            <tbody>
              {searchResults.length === 0 && !searching && (
                <tr><td colSpan={5}>{hasSearched ? 'No providers match your filters.' : 'Run a search to see results.'}</td></tr>
              )}
              {searchResults.length === 0 && searching && <tr><td colSpan={5}>Searching…</td></tr>}
              {searchResults.map((p) => (
                <tr key={p.id}>
                  <td>{[p.last_name, p.first_name].filter(Boolean).join(', ') || '—'}</td>
                  <td>{p.provider_type_name || p.provider_type_code || '—'}</td>
                  <td>{p.email || '—'}</td>
                  <td>{p.payee_name || '—'}</td>
                  <td>{p.payee_email || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
            </>
          )}
          {providerSearchMode === 'proven' && (
            <>
          <div className="provider-search-filters search-form provider-search-proven-filters">
            <label className="provider-search-filter-provider-types">
              <span className="provider-search-filter-label-row">
                Service types (need)
                {provenNeedTypeIds.length > 0 && (
                  <button type="button" className="provider-search-filter-clear" onClick={() => setProvenNeedTypeIds([])}>Clear</button>
                )}
              </span>
              <select
                multiple
                value={provenNeedTypeIds}
                onChange={(e) => setProvenNeedTypeIds([...e.target.selectedOptions].map((o) => o.value))}
                title="Hold Ctrl/Cmd to select multiple"
              >
                {providerTypes.filter(isClientServiceNeedType).map((pt) => (
                  <option key={pt.id} value={String(pt.id)}>{pt.name || pt.code}</option>
                ))}
              </select>
            </label>
            <label>
              <span className="provider-search-filter-label-row">
                Service areas (need)
                {provenServiceAreaIds.length > 0 && (
                  <button type="button" className="provider-search-filter-clear" onClick={() => setProvenServiceAreaIds([])}>Clear</button>
                )}
              </span>
              <select
                multiple
                value={provenServiceAreaIds}
                onChange={(e) => setProvenServiceAreaIds([...e.target.selectedOptions].map((o) => o.value))}
                title="Hold Ctrl/Cmd to select multiple"
              >
                {serviceAreas.map((sa) => (
                  <option key={sa.id} value={String(sa.id)}>{sa.name || sa.code}</option>
                ))}
              </select>
            </label>
            <button type="button" className="btn-primary" onClick={handleProvenSearchProviders} disabled={provenSearching}>
              {provenSearching ? 'Searching…' : 'Search'}
            </button>
          </div>
          {provenError && (
            <div className="provider-search-error" role="alert">
              {provenError}
            </div>
          )}
          {provenHasSearched && (
            <div className="provider-search-results-summary">
              {provenResults.length === 0
                ? 'No providers match these criteria.'
                : provenResults.length === 1
                  ? '1 provider found'
                  : `${provenResults.length} providers found`}
            </div>
          )}
          <div className="provider-search-results-actions provider-search-proven-actions">
            {provenResults.length > 0 && (
              <button type="button" className="btn-secondary" onClick={handleProvenCopyEmails}>
                Copy provider emails
              </button>
            )}
          </div>
          <table className="provider-search-results-table">
            <thead>
              <tr>
                <th>Provider</th>
                <th>Type (code)</th>
                <th>Provider email</th>
                <th>Phone</th>
                <th>Payee</th>
                <th>Payee email</th>
              </tr>
            </thead>
            <tbody>
              {provenResults.length === 0 && !provenSearching && (
                <tr><td colSpan={6}>{provenHasSearched ? 'No providers match these criteria.' : 'Select type(s) and service area(s), then run Search.'}</td></tr>
              )}
              {provenResults.length === 0 && provenSearching && <tr><td colSpan={6}>Searching…</td></tr>}
              {provenResults.map((p) => (
                <tr key={p.id}>
                  <td>{[p.last_name, p.first_name].filter(Boolean).join(', ') || '—'}</td>
                  <td className="provider-search-proven-type-code">
                    {p.provider_type_code
                      ? String(p.provider_type_code).toUpperCase()
                      : (p.provider_type_name || '—')}
                  </td>
                  <td>{p.email || '—'}</td>
                  <td>{p.phone || '—'}</td>
                  <td>{p.payee_name || '—'}</td>
                  <td>{p.payee_email || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
            </>
          )}
        </section>
          )}

          {showCentralAvailability && !effectiveClientId && activeSection === 'central' && (
            <section className="provider-search-central">
              {canManageCentralAvailability ? (
                <>
                  {centralSectionIpNotify && (
                    <div
                      className={
                        centralSectionIpNotify.variant === 'success'
                          ? 'provider-search-commit-match-success provider-search-central-ip-notify-page-banner'
                          : 'provider-search-central-ip-notify-info provider-search-central-ip-notify-page-banner'
                      }
                      role="status"
                    >
                      {centralSectionIpNotify.text}
                    </div>
                  )}
                  <div className="provider-search-email-by-type">
                    <div className="provider-search-email-by-type-inner">
                    <h3 className="provider-search-email-by-type-title">
                      <span className="provider-search-label-with-info">
                        Email Providers By Type
                        <button
                          type="button"
                          className="provider-search-info-icon"
                          onClick={(e) => { e.preventDefault(); setEmailByTypeInfoOpen((v) => !v); }}
                          aria-expanded={emailByTypeInfoOpen}
                          aria-controls="provider-search-email-by-type-info"
                          aria-label="Toggle email providers by type info"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/></svg>
                        </button>
                      </span>
                    </h3>
                    {emailByTypeInfoOpen && (
                      <p className="provider-search-info-text provider-search-email-by-type-info" id="provider-search-email-by-type-info">
                        Preview an email with current open needs for the selected type (counts by city, family availability; no client names). For each single type, recipients are provider emails for everyone of that type in your provider list (not payee emails). The option &quot;All four (DT, OT, PT, ST) — Agency/Center payees&quot; includes open needs for all four therapy types in one message and sends to payee emails for providers whose type is Agency (AGY) or Center (CTR). From the preview you can send via the server (if configured in Tenant Settings) or cancel.
                      </p>
                    )}
                    <div className="provider-search-email-by-type-row">
                      <label>
                        <span className="provider-search-email-by-type-label">Service type</span>
                        <select
                          value={emailByTypeNeedTypeId}
                          onChange={(e) => { setEmailByTypeNeedTypeId(e.target.value); setEmailByTypeError(null); setEmailByTypeSendError(null); setEmailByTypeSendSuccess(null); }}
                          disabled={sendPreviewLoading}
                        >
                          <option value="">Select type…</option>
                          {centralProviderTypes.filter((pt) => isClientServiceProviderType(pt) && (pt.code || '').toUpperCase() !== 'DGN').map((pt) => (
                            <option key={pt.id} value={pt.id}>{pt.code || pt.name}</option>
                          ))}
                          <option value={CENTRAL_AVAILABILITY_EMAIL_ALL_FOUR_THERAPY_AGY_CTR}>
                            All four (DT, OT, PT, ST) — Agency/Center payees
                          </option>
                        </select>
                      </label>
                      <button
                        type="button"
                        className="btn-primary provider-search-email-by-type-btn"
                        onClick={handleOpenSendPreview}
                        disabled={emailByTypeSendLoading || sendPreviewLoading || !emailByTypeNeedTypeId}
                        title="Preview the email and recipient count; then send via server if desired (configure in Tenant Settings)."
                      >
                        {sendPreviewLoading ? 'Loading…' : 'Preview email'}
                      </button>
                    </div>
                    {emailByTypeError && <p className="provider-search-form-error">{emailByTypeError}</p>}
                    {emailByTypeSendError && <p className="provider-search-form-error">{emailByTypeSendError}</p>}
                    {emailByTypeSendSuccess && <p className="provider-search-form-success">{emailByTypeSendSuccess}</p>}
                    </div>
                    {sendPreviewOpen && (
                      <div
                        className="provider-search-send-preview-overlay"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="send-preview-title"
                        onClick={() => { if (!emailByTypeSendLoading) { setSendPreviewOpen(false); setSendPreviewData(null); } }}
                      >
                        <div className="provider-search-send-preview" onClick={(e) => e.stopPropagation()}>
                          <h3 id="send-preview-title">Preview before sending</h3>
                          {sendPreviewLoading ? (
                            <p>Loading…</p>
                          ) : sendPreviewData ? (
                            <>
                              <p className="provider-search-send-preview-recipients">
                                <strong>{sendPreviewData.recipientCount}</strong> recipient{sendPreviewData.recipientCount !== 1 ? 's' : ''}.{' '}
                                <strong>Step 1:</strong> Copy the BCC list, then <strong>Step 2:</strong> open in email and paste it into the BCC field.
                              </p>
                              {sendPreviewData.recipientEmails && sendPreviewData.recipientEmails.length > 0 && (
                                <div className="provider-search-send-preview-bcc">
                                  <div className="provider-search-send-preview-bcc-header">
                                    <strong>BCC list ({sendPreviewData.recipientEmails.length}):</strong>
                                    <button type="button" className="btn-primary provider-search-send-preview-copy-btn" onClick={handleCopyBccList}>
                                      Copy BCC list
                                    </button>
                                  </div>
                                  <pre>{sendPreviewData.recipientEmails.join('\n')}</pre>
                                </div>
                              )}
                              <div className="provider-search-send-preview-subject">
                                <strong>Subject:</strong> {sendPreviewData.subject}
                              </div>
                              <div className="provider-search-send-preview-body">
                                <strong>Body preview:</strong>
                                <pre>{sendPreviewData.body}</pre>
                              </div>
                              <div className="provider-search-send-preview-actions">
                                <button
                                  type="button"
                                  className="btn-cancel"
                                  onClick={() => { setSendPreviewOpen(false); setSendPreviewData(null); }}
                                >
                                  Cancel
                                </button>
                                <button
                                  type="button"
                                  className="btn-primary"
                                  onClick={handleConfirmSendEmail}
                                >
                                  Open in email
                                </button>
                              </div>
                            </>
                          ) : (
                            <p>No preview data.</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {centralLoading ? (
                    <p className="provider-search-loading">Loading…</p>
                  ) : (
                    <>
                      {(editingCentralId || showCentralAddForm) && (
                        <div
                          className="provider-search-central-modal-overlay"
                          role="dialog"
                          aria-modal="true"
                          aria-labelledby="central-modal-title"
                          onClick={(e) => { if (e.target === e.currentTarget) { closeCentralEdit(); closeCentralAddForm(); } }}
                        >
                          <div className="provider-search-central-modal" onClick={(e) => e.stopPropagation()}>
                            <h3 id="central-modal-title">{editingCentralId ? 'Edit Central Availability' : 'Add Central Availability'}</h3>
                            {(centralFormError || centralSaveMessage || centralFormSaving) && editingCentralId && (
                              <div className="provider-search-central-edit-status">
                                {centralFormSaving && <span className="provider-search-central-status-saving">Saving…</span>}
                                {centralSaveMessage && !centralFormSaving && <span className="provider-search-central-status-saved">{centralSaveMessage}</span>}
                                {centralFormError && <span className="provider-search-form-error">{centralFormError}</span>}
                              </div>
                            )}
                            {centralFormError && showCentralAddForm && <div className="provider-search-form-error">{centralFormError}</div>}
                            {editingCentralId ? (
                              <form onSubmit={async (e) => { e.preventDefault(); try { await performCentralEditSave(); closeCentralEdit(); } catch (_) { /* error already set in state */ } }} className="provider-search-central-modal-form">
                                <div className="list-edit-form-grid provider-search-central-availability-grid">
                                  <label className="provider-search-central-availability-span-full"><span className="form-grid-label-caption">Provider <span className="required">*</span></span>
                                    <SearchableProviderSelect
                                      key={editingCentralId || 'edit'}
                                      id="central-edit-provider"
                                      className="provider-search-central-provider-select"
                                      value={centralEditForm.provider_id || ''}
                                      onChange={(id) => applyCentralProviderSelection(setCentralEditForm, id)}
                                      placeholder="Search provider / payee…"
                                    />
                                  </label>
                                  <label>Type
                                    <input type="text" readOnly className="provider-search-input-readonly" value={centralEditTypeLabel} />
                                  </label>
                                  <label><span className="form-grid-label-caption">Service area{centralEditForm.ip_date && <span className="required"> *</span>}</span>
                                    <select value={centralEditForm.service_area_id} onChange={(e) => setCentralEditForm((f) => ({ ...f, service_area_id: e.target.value }))} required={!!centralEditForm.ip_date}>
                                      <option value="">Select area…</option>
                                      {centralServiceAreas.map((sa) => (
                                        <option key={sa.id} value={sa.id}>{sa.name || sa.code}</option>
                                      ))}
                                    </select>
                                  </label>
                                  <label>IP date
                                    <input type="date" value={centralEditForm.ip_date} onChange={(e) => setCentralEditForm((f) => ({ ...f, ip_date: e.target.value }))} />
                                  </label>
                                  <label>LVV date
                                    <input type="date" value={centralEditForm.lvv_date} onChange={(e) => setCentralEditForm((f) => ({ ...f, lvv_date: e.target.value }))} />
                                  </label>
                                  <label className="form-grid-check provider-search-central-availability-span-full">
                                    <input type="checkbox" checked={centralEditForm.also_feeding} onChange={(e) => setCentralEditForm((f) => ({ ...f, also_feeding: e.target.checked }))} />
                                    <span>Also feeding</span>
                                  </label>
                                </div>
                                <label className="provider-search-central-form-full">
                                  <span className="provider-search-label-with-info">
                                    LVV history
                                    <InfoIconButton
                                      ariaExpanded={centralLvvHistoryHintOpen}
                                      ariaControls="central-lvv-history-hint"
                                      ariaLabel="Show explanation"
                                      ariaLabelExpanded="Hide explanation"
                                      onClick={() => setCentralLvvHistoryHintOpen((v) => !v)}
                                    />
                                  </span>
                                  {centralLvvHistoryHintOpen && (
                                    <p id="central-lvv-history-hint" className="form-hint" style={{ marginTop: '0.25rem', marginBottom: '0.35rem' }}>
                                      Updated automatically: when you change LVV date and save, the previous date is added here.
                                    </p>
                                  )}
                                  <textarea readOnly className="provider-search-input-readonly" value={centralEditForm.lvv_history} rows={4} />
                                </label>
                                <label className="provider-search-central-form-full">
                                  Provider comment
                                  <input type="text" value={centralEditForm.provider_comment} onChange={(e) => setCentralEditForm((f) => ({ ...f, provider_comment: e.target.value }))} />
                                </label>
                                <div className="provider-search-central-modal-actions">
                                  {canDeleteCentralAvailability && (
                                    <button type="button" className="btn-danger" onClick={handleCentralDelete} disabled={centralDeleting}>
                                      {centralDeleting ? 'Deleting…' : 'Delete'}
                                    </button>
                                  )}
                                  <button type="button" className="btn-cancel" onClick={() => { closeCentralEdit(); closeCentralAddForm(); }}>Cancel</button>
                                  <button type="submit" className="btn-primary" disabled={centralFormSaving}>{centralFormSaving ? 'Saving…' : 'Save'}</button>
                                </div>
                              </form>
                            ) : (
                              <form onSubmit={handleCentralAddSubmit} className="provider-search-central-modal-form">
                                <div className="list-edit-form-grid provider-search-central-availability-grid">
                                  <label className="provider-search-central-availability-span-full"><span className="form-grid-label-caption">Provider <span className="required">*</span></span>
                                    <SearchableProviderSelect
                                      key={`central-add-${centralAddProviderFieldKey}`}
                                      id="central-add-provider"
                                      className="provider-search-central-provider-select"
                                      value={centralForm.provider_id || ''}
                                      onChange={(id) => applyCentralProviderSelection(setCentralForm, id)}
                                      placeholder="Search provider / payee…"
                                    />
                                  </label>
                                  <label>Type
                                    <input type="text" readOnly className="provider-search-input-readonly" value={centralAddTypeLabel} />
                                  </label>
                                  <label><span className="form-grid-label-caption">Service area{centralForm.ip_date && <span className="required"> *</span>}</span>
                                    <select value={centralForm.service_area_id} onChange={(e) => setCentralForm((f) => ({ ...f, service_area_id: e.target.value }))} required={!!centralForm.ip_date}>
                                      <option value="">Select area…</option>
                                      {centralServiceAreas.map((sa) => (
                                        <option key={sa.id} value={sa.id}>{sa.name || sa.code}</option>
                                      ))}
                                    </select>
                                  </label>
                                  <label>IP date
                                    <input type="date" value={centralForm.ip_date} onChange={(e) => setCentralForm((f) => ({ ...f, ip_date: e.target.value }))} />
                                  </label>
                                  <label>LVV date
                                    <input type="date" value={centralForm.lvv_date} onChange={(e) => setCentralForm((f) => ({ ...f, lvv_date: e.target.value }))} />
                                  </label>
                                  <label className="form-grid-check provider-search-central-availability-span-full">
                                    <input type="checkbox" checked={centralForm.also_feeding} onChange={(e) => setCentralForm((f) => ({ ...f, also_feeding: e.target.checked }))} />
                                    <span>Also feeding</span>
                                  </label>
                                </div>
                                <label className="provider-search-central-form-full">
                                  LVV history
                                  <textarea value={centralForm.lvv_history} onChange={(e) => setCentralForm((f) => ({ ...f, lvv_history: e.target.value }))} rows={2} />
                                </label>
                                <label className="provider-search-central-form-full">
                                  Provider comment
                                  <input type="text" value={centralForm.provider_comment} onChange={(e) => setCentralForm((f) => ({ ...f, provider_comment: e.target.value }))} />
                                </label>
                                <div className="provider-search-central-modal-actions">
                                  <button type="button" className="btn-cancel" onClick={() => { closeCentralEdit(); closeCentralAddForm(); }}>Cancel</button>
                                  <button type="submit" className="btn-primary" disabled={centralFormSaving}>{centralFormSaving ? 'Saving…' : 'Add'}</button>
                                </div>
                              </form>
)}
                      </div>
                        </div>
                      )}

                      <div className="provider-search-central-summary-row">
                        <span className="provider-search-central-summary">
                          {centralProviderPayeeSearch.trim()
                            ? (displayedCentralList.length === 0
                                ? 'No matches on this page'
                                : displayedCentralList.length === 1
                                  ? '1 record (filtered)'
                                  : `${displayedCentralList.length} records (filtered)`)
                            : centralTotal === 0
                              ? 'No records'
                              : centralTotal === 1
                                ? '1 record'
                                : `${centralTotal} records`}
                        </span>
                        <button type="button" className="btn-primary provider-search-central-add-row-btn" onClick={openCentralAddForm}>
                          + Add Provider
                        </button>
                      </div>
                      <table className="provider-search-table provider-search-central-table">
                        <thead>
                          <tr>
                            <th className="provider-search-central-th-sortable" onClick={() => handleCentralSort('provider')} title="Sort by Provider / Payee">
                              Provider / Payee {centralSortBy === 'provider' && (centralSortDir === 'asc' ? ' ↑' : ' ↓')}
                            </th>
                            <th className="provider-search-central-th-sortable" onClick={() => handleCentralSort('type')} title="Sort by Type">
                              Type {centralSortBy === 'type' && (centralSortDir === 'asc' ? ' ↑' : ' ↓')}
                            </th>
                            <th className="provider-search-central-th-sortable" onClick={() => handleCentralSort('service_area')} title="Sort by Service area">
                              Service area {centralSortBy === 'service_area' && (centralSortDir === 'asc' ? ' ↑' : ' ↓')}
                            </th>
                            <th className="provider-search-central-th-sortable" onClick={() => handleCentralSort('ip_date')} title="Sort by IP date">
                              IP date {centralSortBy === 'ip_date' && (centralSortDir === 'asc' ? ' ↑' : ' ↓')}
                            </th>
                            <th className="provider-search-central-th-sortable" onClick={() => handleCentralSort('lvv_date')} title="Sort by LVV date">
                              LVV date {centralSortBy === 'lvv_date' && (centralSortDir === 'asc' ? ' ↑' : ' ↓')}
                            </th>
                            <th className="provider-search-central-th-sortable" onClick={() => handleCentralSort('comment')} title="Sort by Comment">
                              Comment {centralSortBy === 'comment' && (centralSortDir === 'asc' ? ' ↑' : ' ↓')}
                            </th>
                          </tr>
                          <tr className="provider-search-central-filter-row">
                            <th>
                              <input
                                type="text"
                                className="provider-search-central-filter-input"
                                placeholder="Search provider or payee…"
                                value={centralProviderPayeeSearch}
                                onChange={(e) => setCentralProviderPayeeSearch(e.target.value)}
                                aria-label="Filter by provider or payee"
                              />
                            </th>
                            <th>
                              <select
                                className="provider-search-central-filter-select provider-search-central-filter-select-type"
                                value={centralTypeFilter}
                                onChange={(e) => { setCentralTypeFilter(e.target.value); setCentralPage(1); }}
                                aria-label="Filter by type"
                              >
                                <option value="">All types</option>
                                {centralProviderTypes
                                  .filter(isClientServiceProviderType)
                                  .filter((pt) => (pt.code || '').toUpperCase() !== 'DGN')
                                  .map((pt) => (
                                    <option key={pt.id} value={pt.id}>{pt.code || pt.name}</option>
                                  ))}
                              </select>
                            </th>
                            <th>
                              <select
                                className="provider-search-central-filter-select"
                                value={centralAreaFilter}
                                onChange={(e) => { setCentralAreaFilter(e.target.value); setCentralPage(1); }}
                                aria-label="Filter by service area"
                              >
                                <option value="">All areas</option>
                                {centralServiceAreas.map((sa) => (
                                  <option key={sa.id} value={sa.id}>{sa.name || sa.code}</option>
                                ))}
                              </select>
                            </th>
                            <th colSpan={4} />
                          </tr>
                        </thead>
                        <tbody>
                          {displayedCentralList.length === 0 ? (
                            <tr><td colSpan={6}>No rows. Use "+ Add Provider" above to add central availability.</td></tr>
                          ) : (
                            displayedCentralList.map((row) => (
                              <tr
                                key={row.id}
                                className="provider-search-central-row-clickable"
                                onClick={() => openCentralEdit(row)}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openCentralEdit(row); } }}
                                title="Click to edit"
                              >
                                <td>
                                  {([row.provider_last_name, row.provider_first_name].filter(Boolean).join(', ') + (row.payee_name ? ` / ${row.payee_name}` : '')) || '—'}
                                </td>
                                <td>{row.need_type_code || row.need_type_name || '—'}</td>
                                <td>{row.service_area_name || row.service_area_code || '—'}</td>
                                <td>{formatDisplayDate(row.ip_date)}</td>
                                <td>{formatDisplayDate(row.lvv_date)}</td>
                                <td>{(row.provider_comment || '').slice(0, 40)}{(row.provider_comment && row.provider_comment.length > 40) ? '…' : ''}</td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </>
                  )}
                </>
              ) : (
                <p className="provider-search-coming-soon">You don’t have permission to manage central availability.</p>
              )}
            </section>
          )}
        </div>
      </div>
    </div>
  );
}
